import { useEffect, useState, useRef, useCallback } from 'react';
import { api } from '../api/client';
import { useVaults } from '../hooks/useVaults';
import { VaultAccessModal } from '../components/VaultAccessModal';
import { SearchableVaultDropdown } from '../components/SearchableVaultDropdown';
import { DigitalNotaryModal } from '../components/DigitalNotaryModal';
import { DocumentGovernanceInspector } from '../components/DocumentGovernanceInspector';

// ─── Types ────────────────────────────────────────────────────────────────────

interface OutputUrls {
  md?: string;
  docx?: string;
  pdf?: string;
  xlsx?: string;
}

interface DocumentInfo {
  id: string;
  filename: string;
  source_type: string;
  status: string;        // queued | processing | ready | error
  pipeline_type?: string; // ocr_to_md | standard
  disk_type?: 'cloud' | 'digitization' | 'hybrid';
  parent_id?: string;
  version: number;
  chunk_count: number;
  page_count?: number;
  is_notarized?: boolean;
  content_hash?: string;
  metadata?: {
    page_count?: number;
    output_urls?: OutputUrls;
    pipeline_type?: string;
    ocr_mode?: string;
    is_notarized?: boolean;
    latest_certificate_id?: string;
    latest_notarized_at?: string;
  };
  created_at: string;
  output_urls?: OutputUrls;
  // Extended Sharing & Cross-Tenant Fields
  share_id?: string;
  share_permissions?: string;
  allow_download?: boolean;
  watermark_enabled?: boolean;
  invite_status?: string;
  visibility?: string;
  owner_name?: string;
  owner_email?: string;
}

interface UploadItem {
  name: string;
  size: number;
  status: string;
  pipeline?: string;
}

// ─── Markdown ↔ HTML Converter ────────────────────────────────────────────────

function markdownToHtml(md: string): string {
  let html = md
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/^#{3}\s+(.+)$/gm, '<h3>$1</h3>')
    .replace(/^#{2}\s+(.+)$/gm, '<h2>$1</h2>')
    .replace(/^#{1}\s+(.+)$/gm, '<h1>$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^---$/gm, '<hr/>')
    .replace(/^- (.+)$/gm, '<li>$1</li>');

  // Wrap consecutive <li> tags in <ul>
  html = html.replace(/(<li>.*<\/li>\n?)+/g, match => `<ul>${match}</ul>`);

  // Tables
  html = html.replace(/(\|.+\|\n\|[-: |]+\|\n(?:\|.+\|\n?)*)/g, (tableBlock) => {
    const lines = tableBlock.trim().split('\n').filter(l => l.trim() && !l.match(/^\|[-: |]+\|$/));
    if (lines.length === 0) return tableBlock;
    const [headerLine, ...bodyLines] = lines;
    const headers = headerLine.split('|').filter(Boolean).map(h => `<th>${h.trim()}</th>`).join('');
    const rows = bodyLines.map(line => {
      const cells = line.split('|').filter(Boolean).map(c => `<td>${c.trim()}</td>`).join('');
      return `<tr>${cells}</tr>`;
    }).join('');
    return `<table border="1" style="border-collapse:collapse;width:100%;margin:8px 0"><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table>`;
  });

  // Mermaid blocks
  html = html.replace(/```mermaid\n([\s\S]*?)```/g, (_, code) =>
    `<div class="mermaid-block" data-code="${encodeURIComponent(code.trim())}" contenteditable="false" style="background:var(--bg-elevated);border:1px solid var(--border);border-radius:8px;padding:12px;margin:8px 0;font-family:var(--font-mono);font-size:12px;white-space:pre;cursor:pointer" title="Click to edit Mermaid">📊 Mermaid: ${code.trim().split('\n')[0]}</div>`
  );

  // Paragraphs
  html = html.replace(/^(?!<[h|u|t|l|h|d]).+$/gm, line =>
    line.trim() ? `<p style="margin:4px 0;line-height:1.6">${line}</p>` : ''
  );

  return html;
}

function htmlToMarkdown(el: HTMLElement): string {
  let md = '';
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      md += node.textContent;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      const tag = el.tagName.toLowerCase();
      if (tag === 'h1') { md += `# ${el.textContent}\n\n`; return; }
      if (tag === 'h2') { md += `## ${el.textContent}\n\n`; return; }
      if (tag === 'h3') { md += `### ${el.textContent}\n\n`; return; }
      if (tag === 'strong') { md += `**${el.textContent}**`; return; }
      if (tag === 'em') { md += `*${el.textContent}*`; return; }
      if (tag === 'li') { md += `- ${el.textContent}\n`; return; }
      if (tag === 'ul') { el.childNodes.forEach(walk); md += '\n'; return; }
      if (tag === 'hr') { md += '---\n\n'; return; }
      if (tag === 'p') { md += `${el.textContent}\n\n`; return; }
      if (tag === 'br') { md += '\n'; return; }
      if (tag === 'table') {
        const rows = Array.from(el.querySelectorAll('tr'));
        rows.forEach((row, ri) => {
          const cells = Array.from(row.querySelectorAll('th,td'));
          md += '| ' + cells.map(c => c.textContent?.trim() ?? '').join(' | ') + ' |\n';
          if (ri === 0) md += '|' + ' :--- |'.repeat(cells.length) + '\n';
        });
        md += '\n'; return;
      }
      if (tag === 'div' && el.classList.contains('mermaid-block')) {
        const code = decodeURIComponent(el.getAttribute('data-code') ?? '');
        md += `\`\`\`mermaid\n${code}\n\`\`\`\n\n`; return;
      }
      el.childNodes.forEach(walk);
    }
  };
  el.childNodes.forEach(walk);
  return md.trim();
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatBytes(bytes: number) {
  if (!bytes) return '0 B';
  const k = 1024, sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return (bytes / Math.pow(k, i)).toFixed(1) + ' ' + sizes[i];
}

function getPipelineBadge(doc: DocumentInfo) {
  const pt = doc.pipeline_type ?? doc.metadata?.pipeline_type ?? 'standard';
  const ocrMode = doc.metadata?.ocr_mode;

  if (pt === 'ocr_to_md') {
    if (ocrMode === 'ericpro') {
      return { icon: '🛡️', label: 'EricPro', color: '#a855f7' };
    }
    return { icon: '⚡', label: 'EricFlash', color: 'var(--accent-cyan)' };
  }
  return { icon: '📄', label: 'EricLite', color: '#10b981' };
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'ready':      return { cls: 'badge badge--green', label: '✅ Ready' };
    case 'processing': return { cls: 'badge badge--amber', label: '⚙️ Processing' };
    case 'queued':     return { cls: 'badge badge--gray',  label: '⏳ Queued' };
    case 'error':      return { cls: 'badge badge--red',   label: '❌ Error' };
    default:           return { cls: 'badge badge--gray',  label: status };
  }
}

// ─── WYSIWYG Editor Modal ─────────────────────────────────────────────────────

interface EditorProps {
  doc: DocumentInfo;
  initialMd: string;
  initialTab?: 'word' | 'excel' | 'pdf' | 'chat';
  onClose: () => void;
  onSaved: (md: string) => void;
}

interface ExcelTable {
  id: string;
  headers: string[];
  rows: string[][];
}

function parseMarkdownTables(md: string): ExcelTable[] {
  const tables: ExcelTable[] = [];
  const lines = md.split('\n');
  let currentTable: ExcelTable | null = null;
  let inTable = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line.startsWith('|')) {
      if (line.includes('---')) {
        continue;
      }
      const cells = line.split('|').map(c => c.trim()).filter((_, idx, arr) => idx > 0 && idx < arr.length - 1);
      if (!inTable) {
        currentTable = {
          id: `table_${i}`,
          headers: cells,
          rows: []
        };
        inTable = true;
      } else {
        currentTable?.rows.push(cells);
      }
    } else {
      if (inTable && currentTable) {
        tables.push(currentTable);
        currentTable = null;
        inTable = false;
      }
    }
  }
  if (inTable && currentTable) {
    tables.push(currentTable);
  }
  return tables;
}

function serializeExcelTables(md: string, tables: ExcelTable[]): string {
  const lines = md.split('\n');
  let inTable = false;
  let tableIdx = 0;
  let newLines: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line.startsWith('|')) {
      if (!inTable) {
        inTable = true;
        const t = tables[tableIdx++];
        if (t) {
          newLines.push(`| ${t.headers.join(' | ')} |`);
          newLines.push(`| ${t.headers.map(() => '---').join(' | ')} |`);
          t.rows.forEach(row => {
            newLines.push(`| ${row.join(' | ')} |`);
          });
        }
      }
    } else {
      if (inTable) {
        inTable = false;
      }
      newLines.push(lines[i]);
    }
  }
  
  // If tables were added that weren't in the original markdown, append them
  while (tableIdx < tables.length) {
    const t = tables[tableIdx++];
    if (t) {
      newLines.push('\n');
      newLines.push(`| ${t.headers.join(' | ')} |`);
      newLines.push(`| ${t.headers.map(() => '---').join(' | ')} |`);
      t.rows.forEach(row => {
        newLines.push(`| ${row.join(' | ')} |`);
      });
      newLines.push('\n');
    }
  }
  
  return newLines.join('\n');
}

function WysiwygEditor({ doc, initialMd, initialTab, onClose, onSaved }: EditorProps) {
  const [currentDoc, setCurrentDoc] = useState<DocumentInfo>(doc);
  const [showEditorNotary, setShowEditorNotary] = useState(false);
  const [showEditorGovernance, setShowEditorGovernance] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const [wordCount, setWordCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [format, setFormat] = useState<'md' | 'docx' | 'pdf' | 'xlsx'>('md');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFormatMenu, setShowFormatMenu] = useState(false);
  const formatMenuRef = useRef<HTMLDivElement>(null);

  // Document Versioning states
  const [versions, setVersions] = useState<any[]>([]);
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [previewVersion, setPreviewVersion] = useState<any | null>(null);
  const [versionLoading, setVersionLoading] = useState(false);

  const loadVersions = async () => {
    setVersionLoading(true);
    try {
      const res = await api.getDocumentVersions(doc.id);
      setVersions(res.versions || []);
    } catch {}
    finally { setVersionLoading(false); }
  };

  const handleRevertVersion = (v: any) => {
    if (!confirm(`Bạn có chắc chắn muốn phục hồi tài liệu về phiên bản v${v.version_num} (${new Date(v.saved_at).toLocaleString('vi-VN')})?`)) return;
    if (editorRef.current) {
      editorRef.current.innerHTML = markdownToHtml(v.content);
      updateWordCount();
    }
    setShowVersionsModal(false);
    setPreviewVersion(null);
    alert(`✅ Đã phục hồi về phiên bản v${v.version_num}. Hãy bấm "💾 Lưu thay đổi" để áp dụng chính thức.`);
  };

  const handleSyncToPC = async () => {
    const md = getCurrentMd();
    try {
      const res = await fetch('http://127.0.0.1:8765/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'saveFileContent',
          filename: doc.filename.endsWith('.md') ? doc.filename : `${doc.filename}.md`,
          content: md,
          title: doc.filename
        }),
        signal: AbortSignal.timeout(3000)
      }).catch(async () => {
        return fetch('http://127.0.0.1:8765/api/vault/sync', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            filename: doc.filename,
            content: md
          }),
          signal: AbortSignal.timeout(3000)
        });
      });

      if (res && res.ok) {
        alert(`✅ Đã đồng bộ tài liệu "${doc.filename}" về máy tính cá nhân qua EricSpace Bridge!`);
      } else {
        alert('⚠️ EricSpace Bridge phản hồi: Vui lòng kiểm tra lại dịch vụ tại 127.0.0.1:8765');
      }
    } catch {
      alert('Không tìm thấy EricSpace Bridge.\n\nVui lòng cài đặt và chạy EricSpace trên máy tính này (http://127.0.0.1:8765).');
    }
  };
  
  // Tabbed editor states
  const [activeTab, setActiveTab] = useState<'word' | 'excel' | 'pdf' | 'chat'>(initialTab ?? 'word');
  const [excelTables, setExcelTables] = useState<ExcelTable[]>([]);

  // In-Document AI Chat states
  const [inDocChatInput, setInDocChatInput] = useState('');
  const [inDocChatLoading, setInDocChatLoading] = useState(false);
  const [inDocChatMessages, setInDocChatMessages] = useState<Array<{ role: 'user' | 'assistant'; text: string; sources?: any[] }>>([]);
  const inDocChatEndRef = useRef<HTMLDivElement>(null);

  const handleInDocChatSend = async (customPrompt?: string) => {
    const q = (customPrompt || inDocChatInput).trim();
    if (!q || inDocChatLoading) return;
    setInDocChatInput('');
    setInDocChatMessages(prev => [...prev, { role: 'user', text: q }]);
    setInDocChatLoading(true);
    try {
      const activeMd = getCurrentMd() || initialMd;
      const res = await api.query(q, [doc.id], 5, activeMd, doc.filename);
      setInDocChatMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          text: res.answer || res.context || 'Không tìm thấy thông tin phù hợp trong tài liệu này.',
          sources: res.sources
        }
      ]);
    } catch (err: any) {
      setInDocChatMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          text: '⚠️ Lỗi: ' + (err.message || 'Không thể truy vấn tài liệu.')
        }
      ]);
    } finally {
      setInDocChatLoading(false);
      setTimeout(() => inDocChatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  };

  // Load content
  useEffect(() => {
    if (editorRef.current && activeTab === 'word') {
      editorRef.current.innerHTML = markdownToHtml(initialMd);
      updateWordCount();
    }
    const parsedTables = parseMarkdownTables(initialMd);
    setExcelTables(parsedTables);
  }, [initialMd]);

  // Close format menu on outside click
  useEffect(() => {
    if (!showFormatMenu) return;
    const handler = (e: MouseEvent) => {
      if (formatMenuRef.current && !formatMenuRef.current.contains(e.target as Node)) {
        setShowFormatMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showFormatMenu]);

  const updateWordCount = () => {
    const text = editorRef.current?.innerText ?? '';
    setWordCount(text.trim().split(/\s+/).filter(Boolean).length);
  };

  const execCmd = (cmd: string, val?: string) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
    updateWordCount();
  };

  const insertTable = () => {
    const rows = 3, cols = 3;
    const header = '| ' + Array(cols).fill('Tiêu đề').join(' | ') + ' |';
    const sep    = '|' + ' :--- |'.repeat(cols);
    const body   = Array(rows - 1).fill('| ' + Array(cols).fill('Nội dung').join(' | ') + ' |').join('\n');
    const md = `\n${header}\n${sep}\n${body}\n`;
    const html = markdownToHtml(md);
    document.execCommand('insertHTML', false, html);
  };

  const handleSearch = () => {
    if (searchTerm) (window as any).find(searchTerm);
  };

  const getCurrentMd = useCallback(() => {
    if (activeTab === 'word') {
      if (!editorRef.current) return initialMd;
      return htmlToMarkdown(editorRef.current);
    } else if (activeTab === 'excel') {
      const baseMd = editorRef.current ? htmlToMarkdown(editorRef.current) : initialMd;
      return serializeExcelTables(baseMd, excelTables);
    }
    return initialMd;
  }, [activeTab, excelTables, initialMd]);

  const handleTabChange = (newTab: 'word' | 'excel' | 'pdf' | 'chat') => {
    if (newTab === activeTab) return;
    const currentMd = getCurrentMd();
    
    if (newTab === 'word') {
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.innerHTML = markdownToHtml(currentMd);
          updateWordCount();
        }
      }, 0);
    } else if (newTab === 'excel') {
      const parsedTables = parseMarkdownTables(currentMd);
      setExcelTables(parsedTables);
    }
    setActiveTab(newTab);
  };

  const handleSave = async () => {
    const md = getCurrentMd();
    setSaving(true);
    try {
      await api.saveDocumentMarkdown(doc.id, md);
      // Auto snapshot version into history
      await api.saveDocumentVersion(doc.id, {
        content: md,
        summary: `Chỉnh sửa lúc ${new Date().toLocaleTimeString('vi-VN')}`
      }).catch(() => null);
      loadVersions();
      onSaved(md);
    } catch (e: any) {
      alert('Lưu thất bại: ' + (e.message ?? 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = () => {
    if (!editorRef.current) return;
    const md = getCurrentMd();
    const outputUrls = doc.output_urls ?? doc.metadata?.output_urls ?? {};

    if (format === 'md') {
      const blob = new Blob([md], { type: 'text/markdown' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
      a.download = doc.filename.replace(/\.[^.]+$/, '') + '.md'; a.click();
    } else if (format === 'docx' && outputUrls.docx) {
      window.open(outputUrls.docx, '_blank');
    } else if (format === 'pdf' && outputUrls.pdf) {
      window.open(outputUrls.pdf, '_blank');
    } else if (format === 'xlsx' && outputUrls.xlsx) {
      window.open(outputUrls.xlsx, '_blank');
    } else {
      alert('Định dạng này chưa sẵn sàng hoặc tài liệu chưa được xử lý xong.');
    }
  };

  const addRow = (tableIndex: number) => {
    setExcelTables(prev => {
      const next = [...prev];
      const t = { ...next[tableIndex]! };
      t.rows = [...t.rows, Array(t.headers.length).fill('')];
      next[tableIndex] = t;
      return next;
    });
  };

  const addColumn = (tableIndex: number) => {
    setExcelTables(prev => {
      const next = [...prev];
      const t = { ...next[tableIndex]! };
      t.headers = [...t.headers, 'Cột mới'];
      t.rows = t.rows.map(r => [...r, '']);
      next[tableIndex] = t;
      return next;
    });
  };

  const deleteRow = (tableIndex: number, rowIndex: number) => {
    setExcelTables(prev => {
      const next = [...prev];
      const t = { ...next[tableIndex]! };
      t.rows = t.rows.filter((_, idx) => idx !== rowIndex);
      next[tableIndex] = t;
      return next;
    });
  };

  const deleteColumn = (tableIndex: number, colIndex: number) => {
    setExcelTables(prev => {
      const next = [...prev];
      const t = { ...next[tableIndex]! };
      t.headers = t.headers.filter((_, idx) => idx !== colIndex);
      t.rows = t.rows.map(r => r.filter((_, idx) => idx !== colIndex));
      next[tableIndex] = t;
      return next;
    });
  };

  const deleteTable = (tableIndex: number) => {
    if (!confirm('Xoá bảng dữ liệu này?')) return;
    setExcelTables(prev => prev.filter((_, idx) => idx !== tableIndex));
  };

  const createNewTable = () => {
    setExcelTables(prev => [
      ...prev,
      {
        id: `table_new_${Date.now()}`,
        headers: ['Cột A', 'Cột B', 'Cột C'],
        rows: [['', '', ''], ['', '', '']]
      }
    ]);
  };

  const updateCell = (tableIndex: number, rowIndex: number, colIndex: number, val: string) => {
    setExcelTables(prev => {
      const next = [...prev];
      const t = { ...next[tableIndex]! };
      t.rows = t.rows.map((r, ri) => ri === rowIndex ? r.map((c, ci) => ci === colIndex ? val : c) : r);
      next[tableIndex] = t;
      return next;
    });
  };

  const updateHeader = (tableIndex: number, colIndex: number, val: string) => {
    setExcelTables(prev => {
      const next = [...prev];
      const t = { ...next[tableIndex]! };
      t.headers = t.headers.map((h, ci) => ci === colIndex ? val : h);
      next[tableIndex] = t;
      return next;
    });
  };

  const badge = getPipelineBadge(doc);
  const outputUrls = doc.output_urls ?? doc.metadata?.output_urls ?? {};

  const FORMAT_OPTIONS: Array<{ value: 'md'|'docx'|'pdf'|'xlsx'; label: string; ext: string; url?: string }> = [
    { value: 'md',   label: 'Markdown',  ext: '.md',   url: 'local' },
    { value: 'docx', label: 'Word',      ext: '.docx', url: outputUrls.docx },
    { value: 'pdf',  label: 'PDF',       ext: '.pdf',  url: outputUrls.pdf  },
    { value: 'xlsx', label: 'Excel',     ext: '.xlsx', url: outputUrls.xlsx },
  ];
  const currentFmt = FORMAT_OPTIONS.find(f => f.value === format)!;

  return (
    <div className="modal-overlay" style={{ background: 'rgba(5, 5, 12, 0.85)', backdropFilter: 'blur(8px)' }}>
      <style>{`
        .editor-page-a4 {
          width: 210mm;
          min-height: 297mm;
          padding: 25mm 20mm;
          outline: none;
          font-size: 15px;
          line-height: 1.7;
          font-family: var(--font-sans);
          box-shadow: 0 10px 30px rgba(0,0,0,0.3);
          border-radius: 4px;
          box-sizing: border-box;
          overflow-x: auto;
          background-color: white !important;
          color: #1e293b !important;
        }
        /* Phủ nền trắng và chữ tối màu cho toàn bộ các thẻ con bên trong trang A4 */
        .editor-page-a4, .editor-page-a4 * {
          background-color: white !important;
          color: #1e293b !important;
        }
        .editor-page-a4 table {
          width: 100%;
          border-collapse: collapse;
          margin: 16px 0;
          background-color: white !important;
        }
        .editor-page-a4 th, .editor-page-a4 td {
          border: 1px solid #cbd5e1 !important;
          padding: 8px 12px !important;
          text-align: left;
          background-color: white !important;
          color: #1e293b !important;
        }
        .editor-page-a4 th {
          background-color: #f1f5f9 !important;
        }
      `}</style>
      <div className="modal-content" style={{ width: '1100px', maxWidth: '95vw', height: '92vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)' }}>
        
        {/* Editor Header */}
        <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: badge.color }}>{badge.icon}</span> {doc.filename}
            </h2>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
              {wordCount} từ • Lưu lần cuối: {new Date(doc.created_at).toLocaleString('vi-VN')}
            </div>
          </div>
          
          {/* Tabs Control */}
          <div style={{ display: 'flex', background: 'var(--bg-glass)', borderRadius: 'var(--radius-sm)', padding: '3px', border: '1px solid var(--border)' }}>
            <button 
              className={`btn btn--sm ${activeTab === 'word' ? 'btn--primary' : 'btn--ghost'}`}
              style={{ padding: '6px 14px', borderRadius: '6px' }}
              onClick={() => handleTabChange('word')}
            >
              📄 Biên tập Word
            </button>
            <button 
              className={`btn btn--sm ${activeTab === 'excel' ? 'btn--primary' : 'btn--ghost'}`}
              style={{ padding: '6px 14px', borderRadius: '6px' }}
              onClick={() => handleTabChange('excel')}
            >
              📊 Bảng tính Excel
            </button>
            <button 
              className={`btn btn--sm ${activeTab === 'pdf' ? 'btn--primary' : 'btn--ghost'}`}
              style={{ padding: '6px 14px', borderRadius: '6px' }}
              onClick={() => handleTabChange('pdf')}
            >
              🖨️ Xem trước PDF
            </button>
            <button 
              className={`btn btn--sm ${activeTab === 'chat' ? 'btn--primary' : 'btn--ghost'}`}
              style={{ padding: '6px 14px', borderRadius: '6px' }}
              onClick={() => handleTabChange('chat')}
            >
              💬 Hỏi đáp AI
            </button>
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {saving && <span className="spinner" style={{ width: '16px', height: '16px' }} />}
            <button
              className="btn btn--secondary btn--sm"
              onClick={() => {
                setShowVersionsModal(true);
                loadVersions();
              }}
              title="Xem lịch sử và phục hồi phiên bản trước"
            >
              🕒 Lịch sử ({versions.length || 'v1'})
            </button>
            <button
              className="btn btn--secondary btn--sm"
              onClick={handleSyncToPC}
              title="Đồng bộ nội dung này về máy tính qua EricSpace Bridge"
            >
              🔄 Sync PC
            </button>
            <button
              className="btn btn--secondary btn--sm"
              onClick={() => setShowEditorNotary(true)}
              style={{
                background: currentDoc.metadata?.is_notarized ? 'rgba(16, 185, 129, 0.15)' : 'rgba(56, 189, 248, 0.15)',
                border: `1px solid ${currentDoc.metadata?.is_notarized ? 'rgba(16, 185, 129, 0.4)' : 'rgba(56, 189, 248, 0.4)'}`,
                color: currentDoc.metadata?.is_notarized ? '#34d399' : '#38bdf8',
                fontWeight: 600
              }}
              title="Ký số & Cấp chứng thực Blockchain (RFC 3161)"
            >
              {currentDoc.metadata?.is_notarized ? '🛡️ Xem Chứng Nhận Số' : '✍️ Ký Số Blockchain'}
            </button>
            <button
              className="btn btn--secondary btn--sm"
              onClick={() => setShowEditorGovernance(true)}
              style={{
                background: 'rgba(124, 58, 237, 0.15)',
                border: '1px solid rgba(124, 58, 237, 0.4)',
                color: '#c084fc',
                fontWeight: 600
              }}
              title="Quản trị Chiếc Ghế Doanh Nghiệp & Kho Hồ Sơ Vật Lý Song Hành"
            >
              🏛️ Chiếc Ghế & Kho
            </button>
            <button className="btn btn--primary" onClick={handleSave} disabled={saving}>💾 Lưu thay đổi</button>
            <div style={{ position: 'relative' }} ref={formatMenuRef}>
              <button className="btn btn--ghost" onClick={() => setShowFormatMenu(!showFormatMenu)}>
                📥 Tải xuống ({currentFmt.ext})
              </button>
              {showFormatMenu && (
                <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '4px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '8px', padding: '4px', zIndex: 10, minWidth: '150px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                  {FORMAT_OPTIONS.map(f => (
                    <button key={f.value}
                      className="btn btn--ghost"
                      style={{ width: '100%', justifyContent: 'flex-start', padding: '6px 12px', background: format === f.value ? 'var(--bg-glass-hover)' : 'transparent', color: f.url ? 'inherit' : 'var(--text-muted)' }}
                      disabled={!f.url}
                      onClick={() => { setFormat(f.value); setShowFormatMenu(false); setTimeout(handleDownload, 0); }}
                    >
                      {f.label} {f.ext} {!f.url && '(Chưa xử lý)'}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button className="btn btn--ghost" onClick={onClose} style={{ padding: '4px 8px', fontSize: '20px' }}>✕</button>
          </div>
        </div>
        
        {/* Editor Body */}
        {activeTab === 'word' && (
          <>
            <div style={{ display: 'flex', gap: '8px', padding: '8px 24px', borderBottom: '1px solid var(--border)', background: 'var(--bg-elevated)' }}>
              <button className="btn btn--ghost btn--sm" onClick={() => execCmd('formatBlock', 'H1')}>H1</button>
              <button className="btn btn--ghost btn--sm" onClick={() => execCmd('formatBlock', 'H2')}>H2</button>
              <button className="btn btn--ghost btn--sm" onClick={() => execCmd('bold')}><strong>B</strong></button>
              <button className="btn btn--ghost btn--sm" onClick={() => execCmd('italic')}><em>I</em></button>
              <button className="btn btn--ghost btn--sm" onClick={() => execCmd('insertUnorderedList')}>• List</button>
              <button className="btn btn--ghost btn--sm" onClick={() => insertTable()}>🗄️ Bảng</button>
              <div style={{ width: '1px', background: 'var(--border)', margin: '0 8px' }} />
              <div style={{ display: 'flex', alignItems: 'center', background: 'var(--bg-glass)', borderRadius: '4px', padding: '2px 8px', border: '1px solid var(--border)' }}>
                <span style={{ fontSize: '12px', marginRight: '4px' }}>🔍</span>
                <input 
                  type="text" 
                  placeholder="Tìm kiếm..." 
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  style={{ background: 'transparent', border: 'none', outline: 'none', width: '120px', fontSize: '13px' }}
                />
              </div>
            </div>

            {/* A4 page-like view */}
            <div style={{ flex: 1, background: '#181824', overflowY: 'auto', padding: '40px 20px', display: 'flex', justifyContent: 'center' }}>
              <div 
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                onInput={updateWordCount}
                className="editor-page-a4"
              />
            </div>
          </>
        )}

        {activeTab === 'excel' && (
          <div style={{ flex: 1, padding: '24px', overflowY: 'auto', background: '#14142b' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                Biên tập Bảng số liệu trong tài liệu
              </h3>
              <button className="btn btn--ghost btn--sm" onClick={createNewTable} style={{ color: 'var(--accent-cyan)' }}>
                ➕ Thêm bảng tính mới
              </button>
            </div>
            
            {excelTables.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '250px', background: 'var(--bg-glass)', borderRadius: 'var(--radius-md)', border: '1px dashed var(--border)', gap: '12px' }}>
                <span style={{ fontSize: '28px' }}>📊</span>
                <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Không tìm thấy bảng dữ liệu nào trong văn bản này.</div>
                <button className="btn btn--primary btn--sm" onClick={createNewTable}>➕ Tạo bảng tính mới</button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                {excelTables.map((table, tIdx) => (
                  <div key={table.id} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <span style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text-primary)' }}>📊 Bảng dữ liệu #{tIdx + 1}</span>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn btn--ghost btn--sm" onClick={() => addRow(tIdx)} style={{ fontSize: '11px' }}>➕ Thêm dòng</button>
                        <button className="btn btn--ghost btn--sm" onClick={() => addColumn(tIdx)} style={{ fontSize: '11px' }}>➕ Thêm cột</button>
                        <button className="btn btn--danger btn--sm" onClick={() => deleteTable(tIdx)} style={{ fontSize: '11px', padding: '2px 6px' }}>🗑 Xoá bảng</button>
                      </div>
                    </div>
                    
                    <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '600px' }}>
                        <thead>
                          <tr style={{ background: 'var(--bg-glass)', borderBottom: '1px solid var(--border)' }}>
                            {table.headers.map((header, colIdx) => (
                              <th key={colIdx} style={{ padding: '6px', borderRight: '1px solid var(--border)', minWidth: '100px', position: 'relative' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <input 
                                    type="text" 
                                    value={header}
                                    onChange={e => updateHeader(tIdx, colIdx, e.target.value)}
                                    style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--text-primary)', fontWeight: 'bold', outline: 'none', textAlign: 'center' }}
                                  />
                                  <button onClick={() => deleteColumn(tIdx, colIdx)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', opacity: 0.5 }} title="Xoá cột">
                                    🗑
                                  </button>
                                </div>
                              </th>
                            ))}
                            <th style={{ width: '40px' }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {table.rows.map((row, rowIdx) => (
                            <tr key={rowIdx} style={{ borderBottom: '1px solid var(--border)', background: rowIdx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}>
                              {row.map((cell, colIdx) => (
                                <td key={colIdx} style={{ padding: '4px', borderRight: '1px solid var(--border)' }}>
                                  <input 
                                    type="text" 
                                    value={cell}
                                    onChange={e => updateCell(tIdx, rowIdx, colIdx, e.target.value)}
                                    style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--text-primary)', outline: 'none' }}
                                  />
                                </td>
                              ))}
                              <td style={{ textAlign: 'center', padding: '4px' }}>
                                <button onClick={() => deleteRow(tIdx, rowIdx)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', opacity: 0.5 }} title="Xoá dòng">
                                  🗑
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'pdf' && (
          <div style={{ flex: 1, padding: '24px', background: '#181824', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {outputUrls.pdf ? (
              <iframe 
                src={outputUrls.pdf} 
                style={{ width: '100%', height: '100%', border: 'none', background: 'white', borderRadius: 'var(--radius-md)', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }} 
                title="Bản xem trước PDF"
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', color: 'var(--text-secondary)' }}>
                <span className="spinner" style={{ width: '32px', height: '32px' }} />
                <div>Bản xem trước PDF đang được biên dịch lại ở máy chủ. Vui lòng bấm lưu hoặc tải lại sau vài giây...</div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'chat' && (
          <div style={{ flex: 1, display: 'flex', overflow: 'hidden', background: '#0c0d19' }}>
            {/* Left Pane: Document View */}
            <div style={{ flex: 1, borderRight: '1px solid var(--border)', overflowY: 'auto', padding: '24px', background: '#111222' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                  📄 Nội dung tài liệu ({doc.filename})
                </span>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  {wordCount} từ
                </span>
              </div>
              <div 
                style={{ 
                  background: 'white', 
                  color: '#1e293b', 
                  padding: '24px', 
                  borderRadius: '8px', 
                  lineHeight: '1.7', 
                  fontSize: '14px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.3)'
                }}
                dangerouslySetInnerHTML={{ __html: markdownToHtml(getCurrentMd()) }}
              />
            </div>

            {/* Right Pane: AI Chat */}
            <div style={{ width: '420px', flexShrink: 0, display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)' }}>
              
              {/* Chat Pane Header */}
              <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '18px' }}>🤖</span>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600 }}>Trợ lý AI In-Document</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Đang đối soát riêng tài liệu này</div>
                </div>
              </div>

              {/* Quick suggestion chips */}
              <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(255,255,255,0.04)', display: 'flex', gap: '6px', overflowX: 'auto', flexShrink: 0 }}>
                <button
                  className="btn btn--secondary btn--sm"
                  onClick={() => handleInDocChatSend('Tóm tắt các ý chính quan trọng nhất của tài liệu này trong 3-5 gạch đầu dòng.')}
                  style={{ fontSize: '11px', whiteSpace: 'nowrap', padding: '3px 8px' }}
                >
                  ⚡ Tóm tắt ý chính
                </button>
                <button
                  className="btn btn--secondary btn--sm"
                  onClick={() => handleInDocChatSend('Trích xuất toàn bộ các số liệu, ngày tháng và cam kết quan trọng trong văn bản này.')}
                  style={{ fontSize: '11px', whiteSpace: 'nowrap', padding: '3px 8px' }}
                >
                  📊 Trích số liệu
                </button>
                <button
                  className="btn btn--secondary btn--sm"
                  onClick={() => handleInDocChatSend('Có những điều khoản, rủi ro hoặc điểm lưu ý nào tôi cần đặc biệt chú ý?')}
                  style={{ fontSize: '11px', whiteSpace: 'nowrap', padding: '3px 8px' }}
                >
                  ⚠️ Điểm lưu ý
                </button>
              </div>

              {/* Chat Message List */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '14px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {inDocChatMessages.length === 0 ? (
                  <div style={{ margin: 'auto', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12.5px', padding: '20px' }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>💬</div>
                    <div>Hỏi bất kỳ câu hỏi nào về văn bản này.</div>
                    <div style={{ fontSize: '11px', marginTop: '4px' }}>AI sẽ truy vấn trực tiếp từ các đoạn trích của tài liệu.</div>
                  </div>
                ) : (
                  inDocChatMessages.map((msg, i) => (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%' }}>
                      <div style={{
                        padding: '8px 12px',
                        borderRadius: msg.role === 'user' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                        background: msg.role === 'user' ? 'var(--gradient-main)' : 'var(--bg-glass)',
                        border: msg.role === 'user' ? 'none' : '1px solid var(--border)',
                        color: 'var(--text-primary)',
                        fontSize: '13px',
                        lineHeight: '1.6',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}>
                        {msg.text}
                      </div>
                      {msg.sources && msg.sources.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '2px' }}>
                          {msg.sources.map((src, sIdx) => (
                            <span key={sIdx} className="badge badge--blue" style={{ fontSize: '10px', padding: '1px 6px' }}>
                              Trang {src.page || 1} ({(src.score * 100).toFixed(0)}%)
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                )}
                {inDocChatLoading && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-muted)' }}>
                    <span className="spinner" style={{ width: 12, height: 12 }} />
                    <span>Đang đối chiếu tài liệu...</span>
                  </div>
                )}
                <div ref={inDocChatEndRef} />
              </div>

              {/* Chat Input Box */}
              <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border)', display: 'flex', gap: '8px', background: 'var(--bg-elevated)' }}>
                <input
                  className="input"
                  placeholder="Hỏi về tài liệu này..."
                  value={inDocChatInput}
                  onChange={e => setInDocChatInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleInDocChatSend()}
                  style={{ flex: 1, fontSize: '12.5px' }}
                />
                <button
                  className="btn btn--primary btn--sm"
                  onClick={() => handleInDocChatSend()}
                  disabled={inDocChatLoading || !inDocChatInput.trim()}
                >
                  Gửi
                </button>
              </div>

            </div>
          </div>
        )}

        {/* Version History Modal */}
        {showVersionsModal && (
          <div className="modal-overlay" style={{ zIndex: 1000 }}>
            <div className="modal" style={{ maxWidth: '780px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
              <div className="modal__header">
                <h2 className="modal__title">🕒 Lịch Sử Phiên Bản — {doc.filename}</h2>
                <button className="modal__close" onClick={() => { setShowVersionsModal(false); setPreviewVersion(null); }}>✕</button>
              </div>
              <div className="modal__body" style={{ display: 'grid', gridTemplateColumns: previewVersion ? '1fr 1fr' : '1fr', gap: '16px', flex: 1, overflow: 'hidden' }}>
                {/* Version List */}
                <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '50vh' }}>
                  {versionLoading ? (
                    <div style={{ textAlign: 'center', padding: '30px' }}><div className="spinner" /></div>
                  ) : versions.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)', fontSize: '13px' }}>
                      Chưa có snapshot phiên bản nào được lưu. Mỗi khi bạn bấm "💾 Lưu thay đổi", hệ thống sẽ tự động tạo snapshot phiên bản mới.
                    </div>
                  ) : (
                    versions.map(v => {
                      const isSelected = previewVersion?.id === v.id;
                      return (
                        <div
                          key={v.id}
                          onClick={() => setPreviewVersion(v)}
                          style={{
                            background: isSelected ? 'rgba(56, 189, 248, 0.15)' : 'var(--bg-elevated)',
                            border: `1px solid ${isSelected ? 'var(--accent-cyan)' : 'var(--border)'}`,
                            borderRadius: '8px',
                            padding: '12px',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 600, color: '#f8fafc', fontSize: '13px' }}>
                              Phiên bản v{v.version_num}
                            </span>
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                              {new Date(v.saved_at).toLocaleString('vi-VN')}
                            </span>
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                            {v.summary || 'Bản lưu tự động'}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px', fontSize: '11px', color: 'var(--text-muted)' }}>
                            <span>👤 {v.saved_by}</span>
                            <span>{v.word_count || 0} từ • {v.char_count || 0} ký tự</span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Preview & Revert Panel */}
                {previewVersion && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderLeft: '1px solid var(--border)', paddingLeft: '16px', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong style={{ fontSize: '13px', color: 'var(--accent-cyan)' }}>
                        Xem trước v{previewVersion.version_num}
                      </strong>
                      <button
                        className="btn btn--primary btn--sm"
                        onClick={() => handleRevertVersion(previewVersion)}
                      >
                        ⏪ Phục hồi bản này
                      </button>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', background: 'rgba(0,0,0,0.3)', padding: '12px', borderRadius: '6px', fontSize: '12px', color: '#cbd5e1', whiteSpace: 'pre-wrap', fontFamily: 'var(--font-mono)' }}>
                      {previewVersion.content}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal__footer" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button className="btn btn--secondary" onClick={() => { setShowVersionsModal(false); setPreviewVersion(null); }}>Đóng</button>
              </div>
            </div>
          </div>
        )}

        {showEditorNotary && (
          <DigitalNotaryModal
            isOpen={showEditorNotary}
            onClose={() => setShowEditorNotary(false)}
            documents={[currentDoc]}
            preselectedDocId={currentDoc.id}
            onCertificateIssued={(cert) => {
              setCurrentDoc(prev => ({
                ...prev,
                is_notarized: true,
                metadata: { ...prev.metadata, is_notarized: true, latest_certificate_id: cert.certificate_id }
              }));
              if (onSaved) onSaved(getCurrentMd());
            }}
          />
        )}

        {showEditorGovernance && (
          <DocumentGovernanceInspector
            isOpen={showEditorGovernance}
            onClose={() => setShowEditorGovernance(false)}
            document={currentDoc}
          />
        )}

      </div>
    </div>
  );
}

// ─── Share Modal 2.0 ─────────────────────────────────────────────────────────

interface ShareModalProps {
  doc: DocumentInfo;
  onClose: () => void;
}

function ShareModal({ doc, onClose }: ShareModalProps) {
  const [activeTab, setActiveTab] = useState<'link' | 'email' | 'logs'>('link');
  const [shares, setShares] = useState<any[]>([]);
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [creating, setCreating] = useState(false);

  // Link Form State
  const [visibility, setVisibility] = useState('public');
  const [permissions, setPermissions] = useState('view');
  const [password, setPassword] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('0');
  const [allowDownload, setAllowDownload] = useState(true);
  const [watermarkEnabled, setWatermarkEnabled] = useState(false);
  const [maxAccessCount, setMaxAccessCount] = useState('');

  // Email Form State
  const [inviteEmail, setInviteEmail] = useState('');
  const [emailPermissions, setEmailPermissions] = useState('view');
  const [emailAllowDownload, setEmailAllowDownload] = useState(true);
  const [emailWatermark, setEmailWatermark] = useState(false);

  const isFolder = doc.source_type === 'folder';

  const loadShares = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.listShares(doc.id);
      setShares(res.shares || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [doc.id]);

  const loadLogs = useCallback(async () => {
    try {
      setLoadingLogs(true);
      const res = await api.getShareLogs(doc.id);
      setLogs(res.logs || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingLogs(false);
    }
  }, [doc.id]);

  useEffect(() => {
    loadShares();
  }, [loadShares]);

  useEffect(() => {
    if (activeTab === 'logs') loadLogs();
  }, [activeTab, loadLogs]);

  const handleCreateLink = async () => {
    try {
      setCreating(true);
      await api.createShare(doc.id, {
        visibility,
        permissions,
        password: visibility === 'password' ? password : '',
        expires_in_days: parseInt(expiresInDays) || undefined,
        allow_download: allowDownload,
        watermark_enabled: watermarkEnabled,
        max_access_count: maxAccessCount ? parseInt(maxAccessCount) : null
      });
      setVisibility('public');
      setPassword('');
      setExpiresInDays('0');
      setMaxAccessCount('');
      loadShares();
    } catch (e: any) {
      alert('Tạo link chia sẻ thất bại: ' + e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleInviteEmail = async () => {
    if (!inviteEmail.trim()) {
      alert('Vui lòng nhập địa chỉ Email người nhận');
      return;
    }
    try {
      setCreating(true);
      await api.createShare(doc.id, {
        visibility: 'email',
        email: inviteEmail.trim(),
        permissions: emailPermissions,
        allow_download: emailAllowDownload,
        watermark_enabled: emailWatermark
      });
      setInviteEmail('');
      alert(`🎉 Đã tạo quyền truy cập cho ${inviteEmail.trim()}!\n\nKhi người nhận đăng nhập Erix One bằng email này, ${isFolder ? 'thư mục' : 'tài liệu'} sẽ tự động xuất hiện trong mục "Shared with me".`);
      loadShares();
    } catch (e: any) {
      alert('Gửi lời mời thất bại: ' + e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (shareId: string) => {
    if (!confirm('Bạn có chắc chắn muốn thu hồi quyền truy cập này?')) return;
    try {
      await api.deleteShare(shareId);
      loadShares();
    } catch (e: any) {
      alert('Thu hồi quyền thất bại: ' + e.message);
    }
  };

  const copyToClipboard = (shareId: string) => {
    const url = `${window.location.origin}/share/${shareId}`;
    navigator.clipboard.writeText(url);
    alert('Đã copy liên kết chia sẻ vào clipboard!');
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ width: '560px', padding: '24px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '22px' }}>{isFolder ? '📁' : '🔗'}</span>
            <h2 style={{ margin: 0, fontSize: '18px' }}>
              {isFolder ? 'Chia sẻ Thư mục' : 'Chia sẻ Tài liệu'}
            </h2>
          </div>
          <button className="btn btn--ghost btn--sm" onClick={onClose}>✕</button>
        </div>
        
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
          Đang chia sẻ: <strong style={{ color: 'var(--text-primary)' }}>{doc.filename}</strong> {isFolder && <span className="badge badge--purple" style={{ marginLeft: '6px' }}>Thư mục</span>}
        </p>

        {/* Tab Selection */}
        <div style={{ display: 'flex', gap: '4px', background: 'var(--bg-elevated)', padding: '4px', borderRadius: '10px', marginBottom: '20px', border: '1px solid var(--border)' }}>
          <button 
            type="button"
            className="btn btn--sm" 
            style={{ flex: 1, background: activeTab === 'link' ? 'var(--accent-primary)' : 'transparent', color: activeTab === 'link' ? '#fff' : 'var(--text-secondary)' }}
            onClick={() => setActiveTab('link')}
          >
            🔗 Tạo Link Chia Sẻ
          </button>
          <button 
            type="button"
            className="btn btn--sm" 
            style={{ flex: 1, background: activeTab === 'email' ? 'var(--accent-primary)' : 'transparent', color: activeTab === 'email' ? '#fff' : 'var(--text-secondary)' }}
            onClick={() => setActiveTab('email')}
          >
            ✉️ Mời Qua Email
          </button>
          <button 
            type="button"
            className="btn btn--sm" 
            style={{ flex: 1, background: activeTab === 'logs' ? 'var(--accent-primary)' : 'transparent', color: activeTab === 'logs' ? '#fff' : 'var(--text-secondary)' }}
            onClick={() => setActiveTab('logs')}
          >
            📊 Nhật Ký Truy Cập
          </button>
        </div>

        {/* TAB 1: LINK SHARING FORM */}
        {activeTab === 'link' && (
          <div style={{ background: 'var(--bg-elevated)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border)', marginBottom: '24px' }}>
            <h3 style={{ fontSize: '14px', margin: '0 0 12px 0' }}>Tạo Link Mới</h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: 'var(--text-secondary)' }}>Quyền truy cập</label>
                <select className="form-input" value={visibility} onChange={e => setVisibility(e.target.value)}>
                  <option value="public">🌐 Bất kỳ ai có link (Public)</option>
                  <option value="password">🔒 Có Mật khẩu bảo vệ</option>
                  <option value="internal">🏢 Chỉ nội bộ tổ chức</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: 'var(--text-secondary)' }}>Quyền thao tác</label>
                <select className="form-input" value={permissions} onChange={e => setPermissions(e.target.value)}>
                  <option value="view">👁️ Chỉ xem</option>
                  <option value="edit">✏️ Xem & Sửa</option>
                </select>
              </div>
            </div>
            
            {visibility === 'password' && (
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: 'var(--text-secondary)' }}>Mật khẩu mở khóa</label>
                <input type="text" className="form-input" value={password} onChange={e => setPassword(e.target.value)} placeholder="Nhập mật khẩu..." />
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: 'var(--text-secondary)' }}>Thời hạn (0 = Vĩnh viễn)</label>
                <input type="number" min="0" className="form-input" value={expiresInDays} onChange={e => setExpiresInDays(e.target.value)} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: 'var(--text-secondary)' }}>Giới hạn lượt xem (Trống = Vô hạn)</label>
                <input type="number" min="1" className="form-input" placeholder="Ví dụ: 10" value={maxAccessCount} onChange={e => setMaxAccessCount(e.target.value)} />
              </div>
            </div>

            {/* Security Policies */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px 12px', background: 'var(--bg-glass)', borderRadius: '8px', border: '1px solid var(--border)', marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', cursor: 'pointer' }}>
                <input type="checkbox" checked={allowDownload} onChange={e => setAllowDownload(e.target.checked)} />
                <span>📥 Cho phép tải file gốc về máy tính</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', cursor: 'pointer' }}>
                <input type="checkbox" checked={watermarkEnabled} onChange={e => setWatermarkEnabled(e.target.checked)} />
                <span>🛡️ Đóng dấu mờ bản quyền (Watermark chống chụp màn hình)</span>
              </label>
            </div>
            
            <button className="btn btn--primary" style={{ width: '100%' }} onClick={handleCreateLink} disabled={creating}>
              {creating ? 'Đang tạo link...' : '✨ Tạo Link Chia Sẻ'}
            </button>
          </div>
        )}

        {/* TAB 2: EMAIL INVITE FORM */}
        {activeTab === 'email' && (
          <div style={{ background: 'var(--bg-elevated)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border)', marginBottom: '24px' }}>
            <h3 style={{ fontSize: '14px', margin: '0 0 8px 0' }}>Mời Đích Danh Theo Email (Cross-Tenant)</h3>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px', lineHeight: '1.5' }}>
              Người nhận khi đăng nhập vào Erix One bằng email này sẽ lập tức thấy {isFolder ? 'thư mục' : 'tài liệu'} trong mục <strong>"Shared with me"</strong> mà không cần mở link công khai.
            </p>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: 'var(--text-secondary)' }}>Địa chỉ Email người nhận</label>
              <input 
                type="email" 
                className="form-input" 
                placeholder="dongnghiep@company.com" 
                value={inviteEmail} 
                onChange={e => setInviteEmail(e.target.value)} 
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: 'var(--text-secondary)' }}>Quyền thao tác</label>
              <select className="form-input" value={emailPermissions} onChange={e => setEmailPermissions(e.target.value)}>
                <option value="view">👁️ Chỉ xem (View Only)</option>
                <option value="edit">✏️ Chỉnh sửa & Đóng góp (Contributor)</option>
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px 12px', background: 'var(--bg-glass)', borderRadius: '8px', border: '1px solid var(--border)', marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', cursor: 'pointer' }}>
                <input type="checkbox" checked={emailAllowDownload} onChange={e => setEmailAllowDownload(e.target.checked)} />
                <span>📥 Cho phép tải file gốc về máy tính</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', cursor: 'pointer' }}>
                <input type="checkbox" checked={emailWatermark} onChange={e => setEmailWatermark(e.target.checked)} />
                <span>🛡️ Đóng dấu mờ bản quyền (Watermark)</span>
              </label>
            </div>

            <button className="btn btn--primary" style={{ width: '100%' }} onClick={handleInviteEmail} disabled={creating || !inviteEmail.trim()}>
              {creating ? 'Đang gửi lời mời...' : '✉️ Cấp Quyền & Gửi Vào Hộp Nhận'}
            </button>
          </div>
        )}

        {/* TAB 3: AUDIT LOGS */}
        {activeTab === 'logs' && (
          <div style={{ background: 'var(--bg-elevated)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border)', marginBottom: '24px' }}>
            <h3 style={{ fontSize: '14px', margin: '0 0 12px 0' }}>📊 Lịch Sử Ai Đã Truy Cập</h3>
            {loadingLogs ? (
              <div style={{ textAlign: 'center', padding: '24px' }}><div className="spinner"></div></div>
            ) : logs.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-muted)', fontSize: '13px' }}>
                Chưa có nhật ký truy cập nào được ghi nhận.
              </div>
            ) : (
              <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                      <th style={{ padding: '6px 8px' }}>Thời gian</th>
                      <th style={{ padding: '6px 8px' }}>Người xem / IP</th>
                      <th style={{ padding: '6px 8px' }}>Thao tác</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log: any) => (
                      <tr key={log.id} style={{ borderBottom: '1px solid rgba(148, 163, 184, 0.1)' }}>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
                          {new Date(log.created_at).toLocaleString('vi-VN')}
                        </td>
                        <td style={{ padding: '6px 8px' }}>
                          <div style={{ fontWeight: 500 }}>{log.accessor_name || log.accessor_email || 'Khách vãng lai'}</div>
                          <div style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>IP: {log.ip_address}</div>
                        </td>
                        <td style={{ padding: '6px 8px' }}>
                          <span className={`badge ${log.action === 'view' ? 'badge--blue' : log.action.includes('download') ? 'badge--purple' : 'badge--green'}`}>
                            {log.action}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Existing Shares List */}
        <h3 style={{ fontSize: '14px', margin: '0 0 12px 0' }}>Quyền & Liên kết đang hoạt động</h3>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}><div className="spinner"></div></div>
        ) : shares.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: '13px', background: 'var(--bg-glass)', borderRadius: '8px' }}>
            Mục này chưa được chia sẻ cho ai.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '220px', overflowY: 'auto' }}>
            {shares.map(share => {
              const isEmailShare = share.visibility === 'email';
              return (
                <div key={share.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: 'var(--bg-glass)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                  <div style={{ overflow: 'hidden', paddingRight: '8px' }}>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '4px', flexWrap: 'wrap' }}>
                      <span className={`badge ${isEmailShare ? 'badge--cyan' : 'badge--purple'}`}>
                        {isEmailShare ? `✉️ ${share.shared_with_email}` : share.visibility === 'public' ? '🌐 Public' : share.visibility === 'password' ? '🔒 Password' : '🏢 Internal'}
                      </span>
                      <span className="badge badge--blue">{share.permissions === 'view' ? '👁️ Xem' : '✏️ Sửa'}</span>
                      {!share.allow_download && <span className="badge badge--red" style={{ fontSize: '10px' }}>🚫 Khóa tải</span>}
                      {share.watermark_enabled && <span className="badge badge--green" style={{ fontSize: '10px' }}>🛡️ WM</span>}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                      {share.current_access_count !== undefined && (
                        <span>Lượt xem: <strong>{share.current_access_count}</strong>{share.max_access_count ? ` / ${share.max_access_count}` : ''} • </span>
                      )}
                      <span>Hết hạn: {share.expires_at ? new Date(share.expires_at).toLocaleDateString('vi-VN') : 'Vĩnh viễn'}</span>
                      {share.invite_status === 'pending' && <span style={{ color: '#f59e0b', marginLeft: '6px' }}>⏳ Đang chờ nhận</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    {!isEmailShare && (
                      <button className="btn btn--ghost btn--sm" onClick={() => copyToClipboard(share.id)} title="Copy Link">📋</button>
                    )}
                    <button className="btn btn--ghost btn--sm" onClick={() => handleRevoke(share.id)} title="Thu hồi quyền">🗑</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
// ─── Main Page ────────────────────────────────────────────────────────────────
export function DocumentsPage() {
  const { vaults, activeVaultId, setActiveVaultId, createVault, updateVault, deleteVault } = useVaults();
  const [documents, setDocuments] = useState<DocumentInfo[]>([]);
  const [loading, setLoading]     = useState(true);
  const [uploading, setUploading] = useState<UploadItem[]>([]);
  const [pendingUploadFiles, setPendingUploadFiles] = useState<File[]>([]);
  const [pendingUploadFolder, setPendingUploadFolder] = useState<boolean>(false);
  const [showTierModal, setShowTierModal] = useState<boolean>(false);
  const [batchProgress, setBatchProgress] = useState<{ current: number, total: number, message: string } | null>(null);
  const [selected, setSelected]   = useState<Set<string>>(new Set());
  
  // Edit Modal State
  const [editDoc, setEditDoc]     = useState<DocumentInfo | null>(null);
  const [editMd, setEditMd]       = useState('');
  const [editTab, setEditTab]     = useState<'word' | 'excel' | 'pdf' | 'chat'>('word');
  
  // Share Modal State
  const [shareDoc, setShareDoc] = useState<DocumentInfo | null>(null);
  const [vaultConfig, setVaultConfig] = useState<{ id: string; name: string } | null>(null);
  
  // Digital Notary States
  const [showNotaryModal, setShowNotaryModal] = useState(false);
  const [notarySelectedDocId, setNotarySelectedDocId] = useState<string | null>(null);
  const [viewCertOnly, setViewCertOnly] = useState<any | null>(null);

  const openNotaryForDoc = async (doc: DocumentInfo, viewOnly = false) => {
    setNotarySelectedDocId(doc.id);
    const certId = doc.metadata?.latest_certificate_id;
    if (viewOnly && certId) {
      try {
        const res = await api.verifyCertificate(certId);
        if (res.valid && res.certificate) {
          setViewCertOnly(res.certificate);
        } else {
          setViewCertOnly(null);
        }
      } catch {
        setViewCertOnly(null);
      }
    } else {
      setViewCertOnly(null);
    }
    setShowNotaryModal(true);
  };

  // Enterprise Governance States (Position Matrix & Dual Physical Archive)
  const [showGovernanceModal, setShowGovernanceModal] = useState(false);
  const [govDoc, setGovDoc] = useState<DocumentInfo | null>(null);

  const openGovernanceForDoc = (doc: DocumentInfo) => {
    setGovDoc(doc);
    setShowGovernanceModal(true);
  };

  const [dragOver, setDragOver]   = useState(false);
  const [uploadParentId, setUploadParentId] = useState<string>('');
  const [currentFolderId, setCurrentFolderId] = useState<string>('');
  const [folderHistory, setFolderHistory] = useState<{ id: string; name: string }[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('all');
  const [selectedDisk, setSelectedDisk] = useState<'all' | 'cloud' | 'digitization'>('all');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const isSharedInbox = activeVaultId === 'shared' || vaults.find(v => v.id === activeVaultId)?.type === 'shared';
  const [acceptedShares, setAcceptedShares] = useState<Set<string>>(new Set());

  // Reset folder navigation on vault switch
  useEffect(() => {
    setCurrentFolderId('');
    setFolderHistory([]);
  }, [activeVaultId]);

  const handleRespondInvite = async (shareId: string, status: 'accepted' | 'declined') => {
    try {
      await api.respondShareInvite(shareId, status);
      loadDocuments(true);
      alert(status === 'accepted' ? '🎉 Đã chấp thuận nhận tài liệu/thư mục vào không gian làm việc!' : 'Đã từ chối lời mời.');
    } catch (e: any) {
      alert('Thao tác thất bại: ' + e.message);
    }
  };

  const handleDigitize = async (doc: DocumentInfo) => {
    try {
      await api.digitizeDocument(doc.id, doc.metadata?.ocr_mode || 'ericpro');
      loadDocuments(true);
      alert(`⚡ Đã kích hoạt số hóa, tái dựng file & vector hóa cho "${doc.filename}". Tiến trình RAG đang xử lý trong nền!`);
    } catch (e: any) {
      alert('Lỗi kích hoạt số hóa: ' + e.message);
    }
  };

  const handleUndigitize = async (doc: DocumentInfo) => {
    if (!confirm(`Xác nhận rút "${doc.filename}" khỏi Kho tri thức số hóa AI? File vẫn sẽ được lưu trữ an toàn trên Cloud Disk.`)) return;
    try {
      await api.undigitizeDocument(doc.id);
      loadDocuments(true);
    } catch (e: any) {
      alert('Lỗi: ' + e.message);
    }
  };

  const [ericSpaceBridgeAvailable, setEricSpaceBridgeAvailable] = useState<boolean | null>(null);

  // Check EricSpace Bridge availability on mount
  useEffect(() => {
    fetch('http://127.0.0.1:8765/status', { signal: AbortSignal.timeout(800) })
      .then(r => setEricSpaceBridgeAvailable(r.ok))
      .catch(() => setEricSpaceBridgeAvailable(false));
  }, []);

  const openOnPC = async (doc: DocumentInfo) => {
    // Try local bridge first
    if (ericSpaceBridgeAvailable) {
      try {
        const res = await fetch('http://127.0.0.1:8765/open', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            docType: doc.filename.endsWith('.xlsx') || doc.filename.endsWith('.csv') ? 'spreadsheet'
              : doc.filename.endsWith('.pptx') ? 'presentation' : 'word',
            title: doc.filename
          }),
          signal: AbortSignal.timeout(2000)
        });
        if (res.ok) return;
      } catch {}
    }
    // Fallback: notify user
    alert('Không tìm thấy ErixSpace Bridge.\n\nVui lòng cài đặt Erix_ComputeUse.exe từ space.wowerix.com và chạy nó trên máy tính này.');
  };

  const syncToPC = async (doc: DocumentInfo) => {
    if (ericSpaceBridgeAvailable === false) {
      alert('Không tìm thấy EricSpace Bridge.\n\nVui lòng cài đặt và chạy EricSpace trên máy tính này (127.0.0.1:8765) để sử dụng tính năng Đồng bộ.');
      return;
    }
    try {
      let content = '';
      const outputUrls = doc.output_urls ?? doc.metadata?.output_urls ?? {};
      if (outputUrls.md) {
        const r = await fetch(outputUrls.md).catch(() => null);
        if (r && r.ok) content = await r.text();
      }
      if (!content) {
        content = `# ${doc.filename}\n\nTài liệu đồng bộ từ ErixOne Vault.`;
      }

      const res = await fetch('http://127.0.0.1:8765/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'saveFileContent',
          filename: doc.filename.endsWith('.md') ? doc.filename : `${doc.filename}.md`,
          content,
          title: doc.filename
        }),
        signal: AbortSignal.timeout(3000)
      }).catch(async () => {
        return fetch('http://127.0.0.1:8765/api/vault/sync', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            filename: doc.filename,
            content
          }),
          signal: AbortSignal.timeout(3000)
        });
      });

      if (res && res.ok) {
        alert(`✅ Đã đồng bộ tài liệu "${doc.filename}" về máy tính cá nhân qua EricSpace Bridge!`);
      } else {
        alert('⚠️ EricSpace Bridge phản hồi mã lỗi hoặc dịch vụ chưa sẵn sàng.');
      }
    } catch (e: any) {
      alert(`⚠️ Lỗi đồng bộ: ${e.message}`);
    }
  };

  // ── Load & Poll ─────────────────────────────────────────────────────────────

  const loadDocuments = useCallback((noCache?: boolean) => {
    api.listDocuments(activeVaultId, noCache, currentFolderId || undefined, selectedDisk)
      .then(res => setDocuments(res.documents ?? []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [activeVaultId, currentFolderId, selectedDisk]);

  useEffect(() => {
    loadDocuments();
    const timer = setInterval(() => {
      setDocuments(prev => {
        const hasActive = prev.some(d => d.status === 'queued' || d.status === 'processing');
        if (hasActive) api.listDocuments(activeVaultId, true, currentFolderId || undefined, selectedDisk).then(res => setDocuments(res.documents ?? []));
        return prev;
      });
    }, 5000);
    return () => clearInterval(timer);
  }, [loadDocuments, activeVaultId, currentFolderId, selectedDisk]);

  // ── Upload ──────────────────────────────────────────────────────────────────

  const IMAGE_EXTS = new Set(['jpg','jpeg','png','webp','gif','tiff','bmp','heic']);
  const getPipeline = (name: string) =>
    IMAGE_EXTS.has((name.split('.').pop() ?? '').toLowerCase()) ? 'ocr_to_md' : 'standard';

  const checkIfPdfIsScanned = async (file: File): Promise<boolean> => {
    try {
      const blobSlice = file.slice(0, 256 * 1024);
      const arrayBuffer = await blobSlice.arrayBuffer();
      const textDecoder = new TextDecoder('latin1');
      const sample = textDecoder.decode(new Uint8Array(arrayBuffer));
      
      const btEtMatches = sample.match(/BT[\s\S]{0,500}?ET/g) || [];
      let textOpCount = 0;
      for (const block of btEtMatches) {
        const ops = block.match(/\b(Tj|TJ|'|"|Td|Tm|Tf)\b/g) || [];
        textOpCount += ops.length;
      }
      
      return textOpCount < 10;
    } catch {
      return true;
    }
  };

  const needsOcrChoice = async (files: File[]): Promise<boolean> => {
    for (const file of files) {
      const ext = (file.name.split('.').pop() ?? '').toLowerCase();
      if (IMAGE_EXTS.has(ext)) {
        return true;
      }
      if (ext === 'pdf') {
        const isScanned = await checkIfPdfIsScanned(file);
        if (isScanned) {
          return true;
        }
      }
    }
    return false;
  };

  const executeUpload = async (files: File[], ocrMode: string, forceDisk?: 'cloud' | 'digitization') => {
    const defaultDisk = selectedDisk === 'cloud' ? 'cloud' : 'digitization';
    const targetDisk = forceDisk ?? defaultDisk;
    for (const file of files) {
      if (file.size > 50 * 1024 * 1024) {
        alert(`"${file.name}" vượt quá giới hạn 50MB`); continue;
      }
      const pipeline = getPipeline(file.name);
      const item: UploadItem = { name: file.name, size: file.size, status: 'Đang tải lên...', pipeline };
      setUploading(prev => [...prev, item]);
      try {
        await api.uploadDocument(file, currentFolderId || uploadParentId || undefined, undefined, activeVaultId, ocrMode, targetDisk);
        setUploading(prev => prev.filter(u => u.name !== file.name));
        loadDocuments(true);
      } catch (err: any) {
        setUploading(prev => prev.map(u =>
          u.name === file.name ? { ...u, status: `❌ ${err.message ?? 'Upload thất bại'}` } : u
        ));
      }
    }
  };

  const executeFolderUpload = async (files: File[], ocrMode: string, forceDisk?: 'cloud' | 'digitization') => {
    const defaultDisk = selectedDisk === 'cloud' ? 'cloud' : 'digitization';
    const targetDisk = forceDisk ?? defaultDisk;
    setBatchProgress({ current: 0, total: files.length, message: 'Đang phân tích cây thư mục...' });

    const dirMap = new Map<string, { name: string; parentPath: string | null; id?: string }>();
    
    for (const f of files) {
      const parts = f.webkitRelativePath.split('/');
      parts.pop();
      
      let currentPath = '';
      let parentPath: string | null = null;
      for (const part of parts) {
        currentPath = currentPath ? `${currentPath}/${part}` : part;
        if (!dirMap.has(currentPath)) {
          dirMap.set(currentPath, { name: part, parentPath });
        }
        parentPath = currentPath;
      }
    }

    const sortedDirs = Array.from(dirMap.keys()).sort((a, b) => a.split('/').length - b.split('/').length);
    setBatchProgress({ current: 0, total: files.length, message: `Đang tạo ${sortedDirs.length} thư mục...` });

    for (const path of sortedDirs) {
      const dirInfo = dirMap.get(path)!;
      let parentId = currentFolderId || uploadParentId || undefined;
      if (dirInfo.parentPath) parentId = dirMap.get(dirInfo.parentPath)?.id;
      
      try {
        const res = await api.createFolder(dirInfo.name, parentId, activeVaultId, targetDisk);
        dirInfo.id = res.id;
      } catch (err: any) {
        console.error('Lỗi tạo folder', err);
      }
    }

    let uploadedCount = 0;
    for (const f of files) {
      const parts = f.webkitRelativePath.split('/');
      parts.pop();
      const folderPath = parts.join('/');
      const folderId = dirMap.get(folderPath)?.id || currentFolderId || uploadParentId;
      
      setBatchProgress({ current: uploadedCount + 1, total: files.length, message: `Đang tải lên: ${f.name}` });
      
      if (f.size <= 50 * 1024 * 1024) {
        try {
          await api.uploadDocument(f, folderId || undefined, undefined, activeVaultId, ocrMode, targetDisk);
        } catch (err) {
          console.error('Upload failed for', f.name, err);
        }
      }
      uploadedCount++;
    }
    
    setBatchProgress(null);
    loadDocuments(true);
  };

  const uploadFiles = async (files: File[]) => {
    if (selectedDisk === 'cloud') {
      executeUpload(files, 'ericflash', 'cloud');
      return;
    }
    const needsOcr = await needsOcrChoice(files);
    if (needsOcr) {
      setPendingUploadFiles(files);
      setPendingUploadFolder(false);
      setShowTierModal(true);
    } else {
      executeUpload(files, 'ericflash', 'digitization');
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false);
    await uploadFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) await uploadFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  const handleFolderChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const files = Array.from(e.target.files);
    e.target.value = '';
    
    const hasPaths = files.some(f => f.webkitRelativePath);
    if (!hasPaths) return uploadFiles(files);

    if (selectedDisk === 'cloud') {
      executeFolderUpload(files, 'ericflash', 'cloud');
      return;
    }

    const needsOcr = await needsOcrChoice(files);
    if (needsOcr) {
      setPendingUploadFiles(files);
      setPendingUploadFolder(true);
      setShowTierModal(true);
    } else {
      executeFolderUpload(files, 'ericflash', 'digitization');
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Xoá tài liệu "${name}"?`)) return;
    await api.deleteDocument(id).catch((e: any) => alert(e.message));
    loadDocuments(true);
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!confirm(`Xoá ${selected.size} tài liệu đã chọn?`)) return;
    await Promise.all([...selected].map(id => api.deleteDocument(id)));
    setSelected(new Set());
    loadDocuments(true);
  };

  // ── Editor ──────────────────────────────────────────────────────────────────

  const openEditor = async (doc: DocumentInfo, tab: 'word' | 'excel' | 'pdf' | 'chat' = 'word') => {
    // Try to load .md from output_urls or canonical R2 parsed_text.md path
    const outputUrls = doc.output_urls ?? doc.metadata?.output_urls ?? {};
    const fallbackMdUrl = (doc as any).r2_key
      ? `${(import.meta as any).env?.VITE_API_URL || ''}/r2/${(doc as any).r2_key.replace(/\/original\.[^/]+$/, '/parsed_text.md')}`
      : '';
    const mdUrlToFetch = outputUrls.md || fallbackMdUrl;
    let md = '';
    if (mdUrlToFetch) {
      try {
        const res = await fetch(mdUrlToFetch);
        if (res.ok) {
          md = await res.text();
        }
      } catch { /* fallback below */ }
    }
    if (!md) {
      if (doc.disk_type !== 'digitization' && doc.status === 'ready') {
        api.digitizeDocument(doc.id, doc.metadata?.ocr_mode || 'ericpro').then(() => loadDocuments(true)).catch(() => {});
        md = `# ${doc.filename}\n\n⚡ Hệ thống đang tự động kích hoạt tiến trình Số hóa, Tái dựng cấu trúc (Markdown/Word/Excel/PDF) và Vector hóa RAG cho tài liệu này. Vui lòng chờ khoảng 15-30 giây rồi mở lại.`;
      } else {
        md = `# ${doc.filename}\n\nTài liệu đang được xử lý số hóa & tái dựng. Vui lòng chờ pipeline hoàn tất.`;
      }
    }
    setEditMd(md);
    setEditTab(tab);
    setEditDoc(doc);
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  
  // Filter documents by search query, disk type, and file type before hierarchy build
  const filteredDocuments = documents.filter(doc => {
    if (selectedDisk === 'cloud') {
      if (doc.disk_type === 'digitization') return false;
    } else if (selectedDisk === 'digitization') {
      if (doc.disk_type !== 'digitization' && doc.source_type !== 'folder') return false;
    }
    if (searchQuery && !doc.filename.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (filterType !== 'all') {
      const ext = (doc.filename.split('.').pop() ?? '').toLowerCase();
      if (filterType === 'pdf' && ext !== 'pdf') return false;
      if (filterType === 'image' && !['jpg','jpeg','png','webp'].includes(ext)) return false;
      if (filterType === 'doc' && !['doc','docx','xls','xlsx','txt','md'].includes(ext)) return false;
    }
    return true;
  });

  // Build parent-child hierarchy for rendering
  const parentDocs = filteredDocuments.filter(d => !d.parent_id);
  const childDocsByParent = filteredDocuments.reduce((acc, d) => {
    if (d.parent_id) {
      if (!acc[d.parent_id]) acc[d.parent_id] = [];
      acc[d.parent_id].push(d);
    }
    return acc;
  }, {} as Record<string, DocumentInfo[]>);

  const renderDocuments: DocumentInfo[] = [];
  parentDocs.forEach(p => {
    renderDocuments.push(p);
    if (childDocsByParent[p.id]) {
      renderDocuments.push(...childDocsByParent[p.id]);
    }
  });
  // Include orphaned children at the end just in case
  const renderedIds = new Set(renderDocuments.map(d => d.id));
  const orphanDocs = filteredDocuments.filter(d => d.parent_id && !renderedIds.has(d.id));
  renderDocuments.push(...orphanDocs);

  const hasProcessing = documents.some(d => d.status === 'queued' || d.status === 'processing');

  // KPI Metrics
  const totalChunks = documents.reduce((acc, d) => acc + (d.chunk_count || 0), 0);
  const totalPages = documents.reduce((acc, d) => acc + (d.page_count ?? d.metadata?.page_count ?? 0), 0);
  const readyCount = documents.filter(d => d.status === 'indexed' || d.status === 'completed' || d.status === 'ready').length;
  const cloudCount = documents.filter(d => d.disk_type === 'cloud' || !d.disk_type).length;
  const digitizationCount = documents.filter(d => d.disk_type === 'digitization').length;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'24px' }}>

      {/* Header */}
      <div className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <h1 className="page-title" style={{ margin: 0 }}>📚 Documents</h1>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(30, 41, 59, 0.6)',
              border: '1px solid rgba(148, 163, 184, 0.25)',
              padding: '4px 12px',
              borderRadius: '20px',
              fontSize: '13px',
              color: '#f8fafc',
              fontWeight: 500
            }}>
              <span>{!activeVaultId ? '👤 My Vault (Cá nhân)' : activeVaultId === 'shared' ? '🤝 Shared with me' : `🏢 ${vaults.find(v => v.id === activeVaultId)?.name || 'Business Vault'}`}</span>
            </div>

            {activeVaultId && activeVaultId !== 'shared' && (
              <div style={{ display: 'flex', gap: '6px' }}>
                <button 
                  className="btn btn--secondary btn--sm" 
                  onClick={() => {
                    const vault = vaults.find(v => v.id === activeVaultId);
                    if (vault) setVaultConfig(vault);
                  }} 
                  style={{ fontSize: '12px', padding: '4px 10px' }}
                >
                  ⚙️ Phân quyền
                </button>
                <button 
                  className="btn btn--secondary btn--sm" 
                  onClick={() => {
                    const vault = vaults.find(v => v.id === activeVaultId);
                    if (vault) {
                      const newName = prompt('Nhập tên mới cho Kho báu này:', vault.name);
                      if (newName && newName.trim() && newName.trim() !== vault.name) {
                        updateVault(vault.id, newName.trim());
                      }
                    }
                  }} 
                  style={{ fontSize: '12px', padding: '4px 10px' }}
                >
                  ✏️ Đổi tên
                </button>
                <button 
                  className="btn btn--danger btn--sm" 
                  onClick={() => {
                    const vault = vaults.find(v => v.id === activeVaultId);
                    if (vault && confirm(`Bạn có chắc chắn muốn xóa Kho báu "${vault.name}"? Toàn bộ tài liệu bên trong sẽ bị xóa.`)) {
                      deleteVault(vault.id);
                    }
                  }} 
                  style={{ fontSize: '12px', padding: '4px 10px' }}
                >
                  🗑 Xóa
                </button>
              </div>
            )}

            {!isSharedInbox && (
              <button 
                className="btn btn--ghost btn--sm" 
                onClick={() => {
                  const name = prompt('Nhập tên Vault mới cho Team/Dự án:');
                  if (name) createVault(name, 'business');
                }}
                style={{ fontSize: '12px', color: 'var(--accent-cyan)' }}
              >
                + Tạo Kho báu
              </button>
            )}
          </div>
          <p className="page-subtitle" style={{ marginTop: '8px' }}>
            {isSharedInbox 
              ? '📥 Danh sách các tài liệu và Kho báu mà đồng nghiệp/đối tác đã chia sẻ cho bạn'
              : 'Số hoá tài liệu đa định dạng · Ảnh tự động qua OCR pipeline'}
            {hasProcessing && <span style={{ marginLeft:'10px', color:'var(--accent-primary)', animation:'pulse 1.5s infinite' }}>⚙️ Đang xử lý...</span>}
          </p>
        </div>
        <div style={{ display:'flex', gap:'8px' }}>
          {selected.size > 0 && !isSharedInbox && (
            <button className="btn btn--danger btn--sm" onClick={handleBulkDelete}>
              🗑 Xoá {selected.size} mục
            </button>
          )}
          {isSharedInbox ? (
            <div style={{ background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, border: '1px solid rgba(56, 189, 248, 0.3)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>📥</span> Hộp Nhận Chia Sẻ (Chỉ xem / Tiếp nhận)
            </div>
          ) : (
            <>
              <button 
                className="btn btn--secondary" 
                onClick={async () => {
                  const name = prompt('Nhập tên thư mục mới:');
                  if (name && name.trim()) {
                    try {
                      await api.createFolder(name.trim(), currentFolderId || uploadParentId || undefined, activeVaultId, selectedDisk === 'digitization' ? 'digitization' : 'cloud');
                      loadDocuments(true);
                    } catch (e: any) {
                      alert('Tạo thư mục thất bại: ' + e.message);
                    }
                  }
                }}
              >
                📁 + Thư mục
              </button>
              <button className="btn btn--ghost" onClick={() => folderInputRef.current?.click()}>
                🗂 Tải lên Thư mục
              </button>
              <button className="btn btn--primary" onClick={() => fileInputRef.current?.click()}>
                📤 Tải lên Files
              </button>
              <button
                className="btn btn--secondary"
                onClick={() => cameraInputRef.current?.click()}
                title="Chụp ảnh tài liệu giấy bằng camera điện thoại"
              >
                📷 Scan
              </button>
              <button
                className="btn btn--secondary"
                onClick={() => {
                  setNotarySelectedDocId(null);
                  setViewCertOnly(null);
                  setShowNotaryModal(true);
                }}
                style={{
                  background: 'linear-gradient(135deg, rgba(2, 132, 199, 0.2) 0%, rgba(124, 58, 237, 0.2) 100%)',
                  border: '1px solid rgba(56, 189, 248, 0.4)',
                  color: '#38bdf8',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                title="Chọn tài liệu và cấp chứng thực số / chữ ký blockchain"
              >
                <span>🛡️</span> Cấp Chứng Thực Số
              </button>
              <button
                className="btn btn--secondary"
                onClick={() => {
                  const target = documents.find(d => d.source_type !== 'folder') || documents[0];
                  if (target) {
                    openGovernanceForDoc(target);
                  } else {
                    alert('Chưa có tài liệu nào để quản trị chiếc ghế và kho lưu trữ.');
                  }
                }}
                style={{
                  background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.2) 0%, rgba(6, 182, 212, 0.2) 100%)',
                  border: '1px solid rgba(124, 58, 237, 0.4)',
                  color: '#c084fc',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                title="Quản trị Chiếc Ghế Doanh Nghiệp & Kho Hồ Sơ Vật Lý Song Hành"
              >
                <span>🏛️</span> Chiếc Ghế & Kho Vật Lý
              </button>
            </>
          )}
        </div>
      </div>

      {/* KPI Overview Cards (Compact Banner) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1px', background: 'var(--border)', borderRadius: '12px', border: '1px solid var(--border)', overflow: 'hidden' }}>
        <div style={{ background: 'var(--bg-glass)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ fontSize: '20px', padding: '6px', background: 'rgba(6, 182, 212, 0.1)', borderRadius: '8px' }}>📁</div>
          <div>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Tổng tài liệu</div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#f8fafc' }}>{documents.length} <span style={{ fontSize: '12px', fontWeight: 400, color: 'var(--text-secondary)' }}>mục</span></div>
          </div>
        </div>

        <div style={{ background: 'var(--bg-glass)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }} onClick={() => setSelectedDisk('cloud')}>
          <div style={{ fontSize: '20px', padding: '6px', background: 'rgba(56, 189, 248, 0.1)', borderRadius: '8px' }}>☁️</div>
          <div>
            <div style={{ fontSize: '10px', color: '#38bdf8', fontWeight: 600, textTransform: 'uppercase' }}>Cloud Disk</div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#38bdf8' }}>{cloudCount} <span style={{ fontSize: '12px', fontWeight: 400, color: 'var(--text-secondary)' }}>files</span></div>
          </div>
        </div>

        <div style={{ background: 'var(--bg-glass)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }} onClick={() => setSelectedDisk('digitization')}>
          <div style={{ fontSize: '20px', padding: '6px', background: 'rgba(168, 85, 247, 0.1)', borderRadius: '8px' }}>🧠</div>
          <div>
            <div style={{ fontSize: '10px', color: '#c084fc', fontWeight: 600, textTransform: 'uppercase' }}>Số Hóa (RAG)</div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#c084fc' }}>{digitizationCount} <span style={{ fontSize: '12px', fontWeight: 400, color: 'var(--text-secondary)' }}>indexed</span></div>
          </div>
        </div>

        <div style={{ background: 'var(--bg-glass)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ fontSize: '20px', padding: '6px', background: 'rgba(139, 92, 246, 0.1)', borderRadius: '8px' }}>🧩</div>
          <div>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Tri thức AI</div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#a78bfa' }}>{totalChunks.toLocaleString()} <span style={{ fontSize: '12px', fontWeight: 400, color: 'var(--text-secondary)' }}>đoạn</span></div>
          </div>
        </div>

        <div style={{ background: 'var(--bg-glass)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ fontSize: '20px', padding: '6px', background: 'rgba(74, 222, 128, 0.1)', borderRadius: '8px' }}>🟢</div>
          <div>
            <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>Sẵn sàng hỏi đáp</div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: '#4ade80' }}>{readyCount} / {documents.length}</div>
          </div>
        </div>
      </div>

      {batchProgress && (
        <div style={{ background: 'var(--bg-elevated)', padding: '16px', borderRadius: '12px', border: '1px solid var(--accent-primary)', marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px' }}>
            <span>{batchProgress.message}</span>
            <span style={{ fontWeight: 600 }}>{batchProgress.current} / {batchProgress.total}</span>
          </div>
          <div style={{ background: 'var(--border)', height: '8px', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ 
              background: 'var(--accent-primary)', 
              height: '100%', 
              width: `${(batchProgress.current / batchProgress.total) * 100}%`,
              transition: 'width 0.2s ease'
            }} />
          </div>
        </div>
      )}

      <br/>
      <style>{`
        .tooltip-container:hover .tooltip-content {
          display: flex !important;
        }
      `}</style>
      {/* Drop Zone or Shared Inbox Banner */}
      {isSharedInbox ? (
        <div style={{
          background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.9) 0%, rgba(30, 41, 59, 0.9) 100%)',
          border: '1px dashed rgba(56, 189, 248, 0.4)',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '20px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ fontSize: '32px', padding: '12px', background: 'rgba(56, 189, 248, 0.1)', borderRadius: '16px' }}>📥</div>
            <div>
              <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#38bdf8', margin: '0 0 4px 0' }}>
                Hộp Nhận Tài Liệu Được Chia Sẻ (Shared Inbox)
              </h3>
              <p style={{ fontSize: '12px', color: '#cbd5e1', margin: 0, lineHeight: '1.6' }}>
                Kho lưu trữ tài liệu từ đồng nghiệp chia sẻ. Bấm <strong style={{ color: '#4ade80' }}>✅ Chấp thuận</strong> để nhận về không gian của mình.
              </p>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: '11px', background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', padding: '4px 10px', borderRadius: '20px', fontWeight: 600, border: '1px solid rgba(56, 189, 248, 0.3)' }}>
              🔒 Quyền truy cập: Chỉ tiếp nhận
            </span>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)' }}>📂 Lưu trữ vào Kho báu:</span>
              <SearchableVaultDropdown
                vaults={vaults}
                value={activeVaultId}
                onChange={val => setActiveVaultId(val)}
                rootLabel="📁 Không gian của tôi (Gốc)"
              />
            </div>
            
            <div className="tooltip-container" style={{ position: 'relative', cursor: 'help' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                ℹ️ Luồng xử lý AI
              </span>
              <div className="tooltip-content" style={{ position: 'absolute', bottom: '100%', right: 0, marginBottom: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: '8px', padding: '12px', width: '300px', fontSize: '11px', color: 'var(--text-secondary)', zIndex: 10, display: 'none', flexDirection: 'column', gap: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
                <div style={{ color: '#fff', fontWeight: 600, marginBottom: '4px' }}>Hệ thống số hoá đa định dạng</div>
                <div style={{ display: 'grid', gridTemplateColumns: '20px 1fr', gap: '6px' }}>
                  <span>🖼️</span> <span><strong>Image → MD:</strong> Ảnh (JPG/PNG) xử lý qua Eric Vision OCR</span>
                  <span>📑</span> <span><strong>PDF Native:</strong> Parse nhanh bằng PyMuPDF</span>
                  <span>🖼️</span> <span><strong>PDF Scan:</strong> Ảnh chụp trong PDF được xử lý qua Eric Vision OCR</span>
                  <span>📄</span> <span><strong>Text:</strong> DOCX/XLSX/TXT phân tích cấu trúc tự động</span>
                </div>
              </div>
            </div>
          </div>

          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${dragOver ? 'var(--accent-primary)' : 'var(--border)'}`,
              borderRadius: '12px', padding: '24px 20px', textAlign: 'center',
              background: dragOver ? 'var(--bg-glass-hover)' : 'var(--bg-glass)',
              cursor: 'pointer', transition: 'all 0.2s',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px'
            }}>
            <span style={{ fontSize: '32px' }}>{dragOver ? '📥' : '☁️'}</span>
            <div>
              <h3 style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 4px 0', color: 'var(--text-primary)' }}>
                Kéo thả file hoặc click để tải lên
              </h3>
              <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>
                Hỗ trợ PDF, DOCX, XLSX, TXT, MD, JPG, PNG (Tối đa 50MB)
              </p>
            </div>
            <input type="file" ref={fileInputRef} multiple style={{ display: 'none' }} onChange={handleFileChange} />
            {/* @ts-ignore - React typings miss webkitdirectory */}
            <input type="file" ref={folderInputRef} multiple webkitdirectory="" directory="" style={{ display: 'none' }} onChange={handleFolderChange} />
            <input
              type="file"
              ref={cameraInputRef}
              accept="image/*"
              capture={"environment" as any}
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </div>
        </div>
      )}

      {/* Upload Queue */}
      {uploading.length > 0 && (
        <div className="card" style={{ border:'1px solid var(--border-accent)' }}>
          <div className="card__title">Đang tải lên ({uploading.length})</div>
          <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
            {uploading.map((item, idx) => (
              <div key={idx} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'10px 14px', background:'var(--bg-elevated)', borderRadius:'8px' }}>
                <span style={{ fontSize:'20px' }}>{item.pipeline === 'ocr_to_md' ? '🖼️' : '📄'}</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:'13px', fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.name}</div>
                  <div style={{ fontSize:'11px', color:'var(--text-secondary)' }}>
                    {formatBytes(item.size)} · {item.status}
                    {item.pipeline === 'ocr_to_md' && <span style={{ marginLeft:'6px', color:'var(--accent-primary)' }}>→ OCR Pipeline</span>}
                  </div>
                </div>
                {item.status.startsWith('❌') && (
                  <button className="btn btn--ghost btn--sm"
                    onClick={e => { e.stopPropagation(); setUploading(prev => prev.filter(u => u.name !== item.name)); }}>
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Document Table */}
      <div className="card">
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px' }}>
          <div className="card__title" style={{ margin:0 }}>
            Tài liệu đã số hoá
            {documents.length > 0 && <span style={{ marginLeft:'8px', fontSize:'12px', color:'var(--text-muted)', fontWeight:400 }}>({documents.length})</span>}
          </div>
          {documents.length > 0 && (
            <label style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'12px', color:'var(--text-secondary)', cursor:'pointer' }}>
              <input type="checkbox"
                checked={selected.size === documents.length && documents.length > 0}
                onChange={e => setSelected(e.target.checked ? new Set(documents.map(d => d.id)) : new Set())} />
              Chọn tất cả
            </label>
          )}
        </div>

        {/* Two-Disk Architecture Switcher (Song Đĩa: Cloud Disk vs Digitization Disk) */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
          background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.7) 0%, rgba(15, 23, 42, 0.9) 100%)',
          padding: '12px 18px',
          borderRadius: '12px',
          border: '1px solid rgba(148, 163, 184, 0.2)',
          marginBottom: '16px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: '#94a3b8' }}>💽 Không gian ổ đĩa:</span>
            <div style={{ display: 'flex', background: 'rgba(15, 23, 42, 0.6)', padding: '3px', borderRadius: '8px', border: '1px solid rgba(148, 163, 184, 0.15)' }}>
              <button
                type="button"
                onClick={() => setSelectedDisk('all')}
                style={{
                  padding: '6px 14px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 600,
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  background: selectedDisk === 'all' ? 'var(--accent-cyan)' : 'transparent',
                  color: selectedDisk === 'all' ? '#0f172a' : '#cbd5e1'
                }}
              >
                🌟 Toàn bộ ({documents.length})
              </button>
              <button
                type="button"
                onClick={() => setSelectedDisk('cloud')}
                style={{
                  padding: '6px 14px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 600,
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  background: selectedDisk === 'cloud' ? '#0ea5e9' : 'transparent',
                  color: selectedDisk === 'cloud' ? '#ffffff' : '#94a3b8'
                }}
                title="Lưu trữ tệp tin đám mây thuần túy, $0 AI Compute, xem/sửa OnlyOffice & tải trực tiếp"
              >
                ☁️ Cloud Disk ({cloudCount})
              </button>
              <button
                type="button"
                onClick={() => setSelectedDisk('digitization')}
                style={{
                  padding: '6px 14px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: 600,
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  background: selectedDisk === 'digitization' ? 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)' : 'transparent',
                  color: selectedDisk === 'digitization' ? '#ffffff' : '#94a3b8'
                }}
                title="Kho tri thức số hóa đã OCR, chia chunk và đánh index Vectorize cho AI Q&A RAG"
              >
                🧠 Digitization Disk ({digitizationCount})
              </button>
            </div>
          </div>

          <div style={{ fontSize: '12px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {selectedDisk === 'cloud' && (
              <span style={{ color: '#38bdf8' }}>
                💡 <strong>Cloud Disk:</strong> Lưu trữ tệp tin đám mây thuần túy. Bấm nút <strong>⚡ Số hóa AI</strong> trên dòng file để nạp vào Trí nhớ RAG.
              </span>
            )}
            {selectedDisk === 'digitization' && (
              <span style={{ color: '#c084fc' }}>
                🧠 <strong>Digitization Disk:</strong> Kho tri thức thông minh sẵn sàng cho Agent hệ sinh thái (ErixSpace, EriCoder) truy vấn Semantic RAG.
              </span>
            )}
            {selectedDisk === 'all' && (
              <span>
                💡 Đang hiển thị song song cả 2 không gian: ☁️ Cloud Disk & 🧠 Digitization Disk.
              </span>
            )}
          </div>
        </div>

        {/* Search & Filter Bar */}
        {documents.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '16px', background: 'var(--bg-glass)', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: '220px', background: 'var(--bg-elevated)', padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--border-accent)' }}>
              <span>🔍</span>
              <input
                type="text"
                placeholder="Tìm kiếm tài liệu trong Kho báu này..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{ background: 'transparent', border: 'none', color: '#f8fafc', fontSize: '13px', width: '100%', outline: 'none' }}
              />
              {searchQuery && (
                <span onClick={() => setSearchQuery('')} style={{ cursor: 'pointer', color: 'var(--text-muted)', fontSize: '12px' }}>✕</span>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>🗂️ Lọc theo:</span>
              <button
                onClick={() => setFilterType('all')}
                style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '12px', border: 'none', cursor: 'pointer', background: filterType === 'all' ? 'var(--accent-cyan)' : 'var(--bg-elevated)', color: filterType === 'all' ? '#fff' : 'var(--text-secondary)' }}
              >
                Tất cả ({documents.length})
              </button>
              <button
                onClick={() => setFilterType('pdf')}
                style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '12px', border: 'none', cursor: 'pointer', background: filterType === 'pdf' ? 'var(--accent-cyan)' : 'var(--bg-elevated)', color: filterType === 'pdf' ? '#fff' : 'var(--text-secondary)' }}
              >
                📑 PDF
              </button>
              <button
                onClick={() => setFilterType('doc')}
                style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '12px', border: 'none', cursor: 'pointer', background: filterType === 'doc' ? 'var(--accent-cyan)' : 'var(--bg-elevated)', color: filterType === 'doc' ? '#fff' : 'var(--text-secondary)' }}
              >
                📝 Word / Excel
              </button>
              <button
                onClick={() => setFilterType('image')}
                style={{ padding: '4px 10px', borderRadius: '6px', fontSize: '12px', border: 'none', cursor: 'pointer', background: filterType === 'image' ? 'var(--accent-cyan)' : 'var(--bg-elevated)', color: filterType === 'image' ? '#fff' : 'var(--text-secondary)' }}
              >
                🖼️ Ảnh (OCR)
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ display:'flex', justifyContent:'center', padding:'60px' }}><div className="spinner" /></div>
        ) : renderDocuments.length === 0 ? (
          <div style={{ textAlign:'center', padding:'80px', color:'var(--text-muted)' }}>
            <span style={{ fontSize:'48px', display:'block', marginBottom:'12px' }}>{documents.length === 0 ? '📭' : '🔍'}</span>
            <p>{documents.length === 0 ? 'Chưa có tài liệu nào. Kéo thả file để bắt đầu.' : 'Không tìm thấy tài liệu nào khớp với từ khóa tìm kiếm / bộ lọc.'}</p>
            {documents.length === 0 && <p style={{ fontSize:'12px', marginTop:'8px' }}>Ảnh JPG/PNG sẽ tự động qua pipeline OCR → Markdown</p>}
          </div>
        ) : (
          <>
            {/* Folder Breadcrumb Navigation */}
            {folderHistory.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', background: 'var(--bg-elevated)', borderRadius: '10px', border: '1px solid var(--border)', marginBottom: '16px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Vị trí:</span>
                <button 
                  type="button"
                  className="btn btn--ghost btn--sm" 
                  style={{ padding: '2px 8px', fontSize: '12px' }}
                  onClick={() => {
                    setCurrentFolderId('');
                    setFolderHistory([]);
                  }}
                >
                  🏠 Thư mục gốc
                </button>
                {folderHistory.map((item, idx) => (
                  <span key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ color: 'var(--text-muted)' }}>/</span>
                    <button 
                      type="button"
                      className="btn btn--ghost btn--sm" 
                      style={{ padding: '2px 8px', fontSize: '12px', fontWeight: idx === folderHistory.length - 1 ? 700 : 400, color: idx === folderHistory.length - 1 ? 'var(--accent-cyan)' : 'inherit' }}
                      onClick={() => {
                        setCurrentFolderId(item.id);
                        setFolderHistory(prev => prev.slice(0, idx + 1));
                      }}
                    >
                      📁 {item.name}
                    </button>
                  </span>
                ))}
                <div style={{ marginLeft: 'auto' }}>
                  <button 
                    type="button"
                    className="btn btn--secondary btn--sm" 
                    style={{ padding: '3px 10px', fontSize: '11px' }}
                    onClick={() => {
                      const parentHistory = folderHistory.slice(0, -1);
                      setFolderHistory(parentHistory);
                      setCurrentFolderId(parentHistory.length > 0 ? parentHistory[parentHistory.length - 1].id : '');
                    }}
                  >
                    ⬅ Quay lại cấp trên
                  </button>
                </div>
              </div>
            )}

            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ width:'40px' }}></th>
                    <th>Tên File / Thư mục</th>
                    <th>CÔNG CỤ</th>
                    <th>Ổ ĐĨA</th>
                    <th>Định dạng</th>
                    <th>Chunks</th>
                    <th>Trang</th>
                    <th>Trạng thái</th>
                    <th>Ngày tạo</th>
                    <th style={{ width:'180px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {renderDocuments.map(doc => {
                    const badge = getStatusBadge(doc.status);
                    const pipeline = getPipelineBadge(doc);
                    const outputUrls = doc.output_urls ?? doc.metadata?.output_urls ?? {};
                    const isChild = !!doc.parent_id;
                    const isFolderItem = doc.source_type === 'folder';
                    
                    return (
                      <tr key={doc.id} style={{ opacity: doc.status === 'error' ? 0.6 : 1, background: isChild ? 'var(--bg-elevated)' : 'transparent' }}>
                        <td style={{ paddingLeft: isChild ? '32px' : '16px' }}>
                          <input type="checkbox" checked={selected.has(doc.id)}
                            onChange={() => toggleSelect(doc.id)} />
                        </td>
                        <td style={{ fontWeight: isFolderItem ? 600 : isChild ? 400 : 600, maxWidth:'260px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {isFolderItem ? (
                            <div 
                              onClick={() => {
                                setCurrentFolderId(doc.id);
                                setFolderHistory(prev => [...prev, { id: doc.id, name: doc.filename }]);
                              }}
                              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                              title="Nhấp để mở thư mục"
                            >
                              {isChild && <span style={{ color:'var(--text-muted)' }}>↳</span>}
                              <span style={{ fontSize: '16px' }}>📁</span>
                              <span style={{ color: 'var(--accent-cyan)', textDecoration: 'underline' }}>{doc.filename}</span>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              {isChild && <span style={{ color:'var(--text-muted)' }}>↳</span>}
                              <span>{doc.source_type === 'pdf' ? '📑' : '📄'}</span>
                              <span>{doc.filename}</span>
                              {(doc.metadata?.is_notarized || doc.is_notarized) && (
                                <span
                                  className="badge badge--green"
                                  style={{ cursor: 'pointer', fontSize: '10px', padding: '2px 6px', display: 'inline-flex', alignItems: 'center', gap: '3px' }}
                                  title="Tài liệu đã được ký số & đóng dấu trên Blockchain. Nhấp để xem Giấy Chứng Nhận Số."
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openNotaryForDoc(doc, true);
                                  }}
                                >
                                  🛡️ Đã Ký Số
                                </span>
                              )}
                            </div>
                          )}
                          {isSharedInbox && doc.owner_name && (
                            <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px', fontWeight: 400 }}>
                              Từ: <strong>{doc.owner_name}</strong> {doc.share_permissions && ` • Quyền: ${doc.share_permissions === 'edit' ? '✏️ Sửa' : '👁️ Xem'}`}
                            </div>
                          )}
                        </td>
                        <td>
                          {isFolderItem ? (
                            <span style={{ fontSize:'12px', color:'var(--text-secondary)' }}>📁 Folder</span>
                          ) : (
                            <span style={{ fontSize:'12px', color: pipeline.color, fontWeight:500 }}>
                              {pipeline.icon} {pipeline.label}
                            </span>
                          )}
                        </td>
                        <td>
                          {isFolderItem ? (
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>📁 Thư mục</span>
                          ) : doc.disk_type === 'digitization' ? (
                            <span 
                              style={{ 
                                padding: '2px 8px', 
                                borderRadius: '12px', 
                                fontSize: '11px', 
                                fontWeight: 600, 
                                background: 'rgba(168, 85, 247, 0.15)', 
                                color: '#c084fc', 
                                border: '1px solid rgba(168, 85, 247, 0.3)', 
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                gap: '4px' 
                              }} 
                              title="Tài liệu đã được số hóa & nạp vào Kho tri thức AI Vector (RAG)"
                            >
                              🧠 Số hóa RAG
                            </span>
                          ) : (
                            <span 
                              style={{ 
                                padding: '2px 8px', 
                                borderRadius: '12px', 
                                fontSize: '11px', 
                                fontWeight: 600, 
                                background: 'rgba(56, 189, 248, 0.15)', 
                                color: '#38bdf8', 
                                border: '1px solid rgba(56, 189, 248, 0.3)', 
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                gap: '4px' 
                              }} 
                              title="Lưu trữ đám mây thuần túy, $0 AI compute"
                            >
                              ☁️ Cloud Disk
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={`badge ${isFolderItem ? 'badge--purple' : 'badge--blue'}`} style={{ textTransform:'uppercase', fontSize:'10px' }}>
                            {doc.source_type}
                          </span>
                        </td>
                        <td style={{ fontFamily:'var(--font-mono)', fontSize:'12px' }}>
                          {isFolderItem ? '—' : (doc.chunk_count || '—')}
                        </td>
                        <td style={{ fontSize:'12px' }}>
                          {isFolderItem ? '—' : (doc.page_count ?? doc.metadata?.page_count ?? '—')}
                        </td>
                        <td><span className={badge.cls}>{badge.label}</span></td>
                        <td style={{ color:'var(--text-muted)', fontSize:'11px', whiteSpace:'nowrap' }}>
                          {new Date(doc.created_at).toLocaleString('vi-VN')}
                        </td>
                        <td>
                          <div style={{ display:'flex', gap:'4px', alignItems: 'center' }}>
                            {isSharedInbox ? (
                              <>
                                {doc.invite_status === 'pending' ? (
                                  <div style={{ display: 'flex', gap: '4px' }}>
                                    <button 
                                      className="btn btn--primary btn--sm" 
                                      style={{ background: '#10b981', fontSize: '11px', padding: '3px 8px' }} 
                                      onClick={() => handleRespondInvite(doc.share_id!, 'accepted')}
                                      title="Chấp thuận nhận vào không gian làm việc"
                                    >
                                      ✅ Nhận
                                    </button>
                                    <button 
                                      className="btn btn--danger btn--sm" 
                                      style={{ fontSize: '11px', padding: '3px 8px' }} 
                                      onClick={() => handleRespondInvite(doc.share_id!, 'declined')}
                                      title="Từ chối"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ) : (
                                  <span style={{ background: 'rgba(74, 222, 128, 0.15)', color: '#4ade80', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600 }}>
                                    Đã nhận
                                  </span>
                                )}

                                {isFolderItem ? (
                                  <button 
                                    className="btn btn--primary btn--sm" 
                                    style={{ fontSize: '11px', padding: '3px 8px' }} 
                                    onClick={() => {
                                      setCurrentFolderId(doc.id);
                                      setFolderHistory(prev => [...prev, { id: doc.id, name: doc.filename }]);
                                    }}
                                  >
                                    📂 Mở
                                  </button>
                                ) : (
                                  <>
                                    <button className="btn btn--ghost btn--sm" onClick={() => openEditor(doc)} title="Xem nội dung">👁️</button>
                                    {doc.share_permissions === 'edit' && (
                                      <button className="btn btn--ghost btn--sm" onClick={() => openEditor(doc)} title="Chỉnh sửa nội dung">✏️</button>
                                    )}
                                    {doc.allow_download !== false && outputUrls.md && (
                                      <a className="btn btn--ghost btn--sm" href={outputUrls.md} target="_blank" title="Tải MD">.md</a>
                                    )}
                                  </>
                                )}
                              </>
                            ) : (
                              <>
                                {isFolderItem ? (
                                  <>
                                    <button 
                                      className="btn btn--primary btn--sm" 
                                      style={{ fontSize: '11px', padding: '3px 8px' }} 
                                      onClick={() => {
                                        setCurrentFolderId(doc.id);
                                        setFolderHistory(prev => [...prev, { id: doc.id, name: doc.filename }]);
                                      }} 
                                      title="Mở thư mục"
                                    >
                                      📂 Mở
                                    </button>
                                    <button className="btn btn--ghost btn--sm" onClick={() => setShareDoc(doc)} title="Chia sẻ thư mục này">🔗</button>
                                    <button className="btn btn--danger btn--sm" onClick={() => handleDelete(doc.id, doc.filename)} title="Xóa thư mục">🗑</button>
                                  </>
                                ) : (
                                  <>
                                    {doc.status === 'ready' && (
                                      <>
                                        {doc.status === 'ready' && doc.disk_type !== 'digitization' && (
                                          <button
                                            className="btn btn--primary btn--sm"
                                            style={{
                                              fontSize: '11px',
                                              padding: '3px 8px',
                                              background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
                                              border: 'none',
                                              color: '#fff',
                                              fontWeight: 600,
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: '3px'
                                            }}
                                            onClick={() => handleDigitize(doc)}
                                            title="Số hóa & nạp tài liệu này vào Kho tri thức AI Vector (RAG)"
                                          >
                                            ⚡ Số hóa AI
                                          </button>
                                        )}
                                        {doc.status === 'ready' && doc.disk_type === 'digitization' && (
                                          <button
                                            className="btn btn--ghost btn--sm"
                                            style={{ fontSize: '11px', padding: '3px 6px', color: '#f87171' }}
                                            onClick={() => handleUndigitize(doc)}
                                            title="Rút tài liệu ra khỏi Kho tri thức số hóa AI (vẫn giữ file an toàn trên Cloud Disk)"
                                          >
                                            ↩ Rút AI
                                          </button>
                                        )}
                                        <button className="btn btn--ghost btn--sm" onClick={() => setShareDoc(doc)} title="Chia sẻ">🔗</button>
                                        <button className="btn btn--ghost btn--sm" onClick={() => {
                                          const target = prompt('Nhập ID Vault muốn di chuyển đến:\n' + vaults.map(v => `${v.name}: ${v.id}`).join('\n'));
                                          if (target) {
                                            api.post(`/api/documents/${doc.id}/move`, { vault_id: target })
                                              .then(() => loadDocuments())
                                              .catch(e => alert(e.message));
                                          }
                                        }} title="Move to Vault">📦</button>
                                        <button className="btn btn--ghost btn--sm" onClick={() => openEditor(doc)} title="Mở WYSIWYG Editor">✏️</button>
                                        <button className="btn btn--ghost btn--sm" onClick={() => openEditor(doc, 'chat')} title="Hỏi đáp AI với riêng tài liệu này">💬</button>
                                        <button
                                          className="btn btn--ghost btn--sm"
                                          onClick={() => openNotaryForDoc(doc)}
                                          title={(doc.metadata?.is_notarized || doc.is_notarized) ? "Xem Giấy Chứng Nhận Số trên Blockchain" : "Ký số & Cấp Chứng Thực Blockchain"}
                                          style={{ color: (doc.metadata?.is_notarized || doc.is_notarized) ? '#10b981' : undefined }}
                                        >
                                          {(doc.metadata?.is_notarized || doc.is_notarized) ? '🛡️' : '✍️'}
                                        </button>
                                        <button
                                          className="btn btn--ghost btn--sm"
                                          onClick={() => openGovernanceForDoc(doc)}
                                          title="Quản trị Chiếc Ghế Doanh Nghiệp & Kho Hồ Sơ Vật Lý Song Hành"
                                          style={{ color: '#c084fc' }}
                                        >
                                          🏛️
                                        </button>
                                        {outputUrls.md    && <a className="btn btn--ghost btn--sm" href={outputUrls.md}   target="_blank" title="Tải MD">.md</a>}
                                        {outputUrls.docx  && <a className="btn btn--ghost btn--sm" href={outputUrls.docx} target="_blank" title="Tải DOCX">.doc</a>}
                                      </>
                                    )}
                                    {(doc.status === 'queued' || doc.status === 'processing') && (
                                      <span className="spinner" style={{ width:'16px', height:'16px', margin:'0 8px' }} />
                                    )}
                                    {ericSpaceBridgeAvailable !== false && (
                                      <>
                                        <button
                                          className="btn btn--secondary btn--sm"
                                          onClick={(e) => { e.stopPropagation(); openOnPC(doc); }}
                                          title="Mở tài liệu này trong OnlyOffice trên máy tính (cần ErixSpace Bridge)"
                                          style={{ fontSize: '12px', padding: '4px 8px' }}
                                        >
                                          🖥️ Mở trên PC
                                        </button>
                                        <button
                                          className="btn btn--ghost btn--sm"
                                          onClick={(e) => { e.stopPropagation(); syncToPC(doc); }}
                                          title="Đồng bộ nội dung tài liệu về thư mục làm việc EricSpace trên máy tính"
                                          style={{ fontSize: '12px', padding: '4px 8px' }}
                                        >
                                          🔄 Sync PC
                                        </button>
                                      </>
                                    )}
                                    <button className="btn btn--danger btn--sm" onClick={() => handleDelete(doc.id, doc.filename)}>🗑</button>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* WYSIWYG Editor */}
      {editDoc && (
        <WysiwygEditor
          doc={editDoc}
          initialMd={editMd}
          initialTab={editTab}
          onClose={() => { setEditDoc(null); setEditMd(''); setEditTab('word'); }}
          onSaved={(md) => { setEditMd(md); loadDocuments(); }}
        />
      )}

      {/* Share Modal */}
      {shareDoc && (
        <ShareModal
          doc={shareDoc}
          onClose={() => setShareDoc(null)}
        />
      )}

      {vaultConfig && (
        <VaultAccessModal vault={vaultConfig} onClose={() => setVaultConfig(null)} />
      )}

      {/* Upload Tier Selection Modal */}
      {showTierModal && (
        <div className="modal-overlay" style={{ background: 'rgba(5, 5, 12, 0.85)', backdropFilter: 'blur(8px)', zIndex: 1000, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="modal-content" style={{ width: '550px', padding: '28px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: '16px', boxShadow: '0 20px 40px rgba(0,0,0,0.5)' }}>
            <h2 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>🤖 Chọn Cấp Độ Xử Lý OCR</h2>
            <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
              Hệ thống phát hiện tài liệu dạng PDF scan hoặc hình ảnh. Vui lòng chọn cấp độ xử lý:
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              {/* EricFlash Card */}
              <div 
                onClick={() => {
                  setShowTierModal(false);
                  if (pendingUploadFolder) {
                    executeFolderUpload(pendingUploadFiles, 'ericflash', 'digitization');
                  } else {
                    executeUpload(pendingUploadFiles, 'ericflash', 'digitization');
                  }
                  setPendingUploadFiles([]);
                }}
                style={{ 
                  background: 'var(--bg-elevated)', 
                  border: '1px solid var(--border)', 
                  borderRadius: '12px', 
                  padding: '24px 20px', 
                  cursor: 'pointer', 
                  textAlign: 'center'
                }}
                className="tier-card-hover"
              >
                <div style={{ fontSize: '36px', marginBottom: '12px' }}>⚡</div>
                <div style={{ fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)', marginBottom: '6px' }}>EricFlash (Số hóa & RAG)</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>Tái dựng MD/Word/Excel/PDF + Vector RAG cho tài liệu sắc nét</div>
              </div>

              {/* EricPro Card */}
              <div 
                onClick={() => {
                  setShowTierModal(false);
                  if (pendingUploadFolder) {
                    executeFolderUpload(pendingUploadFiles, 'ericpro', 'digitization');
                  } else {
                    executeUpload(pendingUploadFiles, 'ericpro', 'digitization');
                  }
                  setPendingUploadFiles([]);
                }}
                style={{ 
                  background: 'var(--bg-elevated)', 
                  border: '1px solid var(--border)', 
                  borderRadius: '12px', 
                  padding: '24px 20px', 
                  cursor: 'pointer', 
                  textAlign: 'center'
                }}
                className="tier-card-hover"
              >
                <div style={{ fontSize: '36px', marginBottom: '12px' }}>🛡️</div>
                <div style={{ fontWeight: 600, fontSize: '15px', color: 'var(--text-primary)', marginBottom: '6px' }}>EricPro (Số hóa Chuyên sâu)</div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>Tái dựng chuẩn xác bảng biểu phức tạp, ảnh mờ & đối soát số liệu</div>
              </div>
            </div>

            <div style={{ background: 'rgba(245, 158, 11, 0.08)', border: '1px solid rgba(245, 158, 11, 0.2)', padding: '14px', borderRadius: '10px', fontSize: '12.5px', color: 'var(--accent-amber)', lineHeight: '1.5', marginBottom: '20px' }}>
              ℹ️ <strong>Chú thích:</strong> Lựa chọn EricFlash hoặc EricPro sẽ tự động <strong>tái dựng tài liệu</strong> (sang Markdown, Word, Excel, PDF) và <strong>vector hóa nạp vào kho RAG</strong>. Nếu chỉ muốn lưu trữ file gốc không tốn AI Token, hãy chọn <em>Chỉ Lưu Cloud Disk</em> bên dưới.
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                className="btn btn--secondary btn--sm"
                onClick={() => {
                  setShowTierModal(false);
                  if (pendingUploadFolder) {
                    executeFolderUpload(pendingUploadFiles, 'none', 'cloud');
                  } else {
                    executeUpload(pendingUploadFiles, 'none', 'cloud');
                  }
                  setPendingUploadFiles([]);
                }}
                style={{ padding: '8px 14px', fontSize: '12.5px' }}
              >
                ☁️ Chỉ Lưu Trữ Gốc (Cloud Disk - $0 AI)
              </button>
              <button 
                className="btn btn--ghost" 
                onClick={() => {
                  setShowTierModal(false);
                  setPendingUploadFiles([]);
                }}
                style={{ padding: '8px 16px', color: 'var(--text-secondary)' }}
              >
                Hủy bỏ
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Digital Notary Modal */}
      {showNotaryModal && (
        <DigitalNotaryModal
          isOpen={showNotaryModal}
          onClose={() => {
            setShowNotaryModal(false);
            setNotarySelectedDocId(null);
            setViewCertOnly(null);
          }}
          documents={documents.filter(d => d.source_type !== 'folder')}
          preselectedDocId={notarySelectedDocId}
          viewCertificateOnly={viewCertOnly}
          onCertificateIssued={(_cert) => {
            loadDocuments(true);
          }}
        />
      )}

      {/* Enterprise Governance & Dual Physical Archive Modal */}
      {showGovernanceModal && govDoc && (
        <DocumentGovernanceInspector
          isOpen={showGovernanceModal}
          onClose={() => {
            setShowGovernanceModal(false);
            setGovDoc(null);
          }}
          document={govDoc}
          onGovernanceUpdated={() => {
            loadDocuments(true);
          }}
        />
      )}

    </div>
  );
}
